import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import ConfirmModal from "../components/ConfirmModal";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithCredential,
  signInWithPopup,
  sendEmailVerification,
  setPersistence,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  linkWithCredential,
  linkWithPopup,
  unlink,
  type User as FirebaseUser,
  type UserInfo,
} from "firebase/auth";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { Capacitor } from "@capacitor/core";
import {
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  runTransaction,
  getDocs,
  collection,
  query,
  where,
  updateDoc,
  onSnapshot,
  writeBatch,
  arrayUnion,
  arrayRemove,
  addDoc,
  serverTimestamp,
  orderBy,
  limit,
  startAfter,
  or,
} from "firebase/firestore";
import { auth, db, crashlytics, analytics } from "../services/firebase";
import { type User, type UserAction } from "../types";
import { PushNotificationManager } from "../utils/pushNotificationManager";
import {
  isApplePrivateRelay,
  shouldSuggestAccountLinking,
} from "../utils/privateRelayHelper";
import {
  getSignupMetadata,
  getPermissionSnapshot,
  getLoginMetadata,
  type SignupMetadata,
} from "../utils/deviceInfo";
import { CommonFunctions } from "../utils/commonUtils";
import { queueUserAction } from "../services/userActionService";
import { App as CapacitorApp } from "@capacitor/app";
import { APP_VERSION } from "../version";
import {
  applyOptimisticAcceptFollowRequest,
  applyOptimisticCancelFollowRequest,
  applyOptimisticDeclineFollowRequest,
  applyOptimisticMutualFollow,
  applyOptimisticRemoveFollower,
  applyOptimisticRequestFollow,
  applyOptimisticUnfollow,
} from "../utils/socialOptimisticState";

const NATIVE_AUTH_TIMEOUT_MS = 6000;

async function withNativeAuthTimeout<T>(promise: Promise<T>): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      const timeoutError = new Error("auth/timeout");
      (timeoutError as Error & { code?: string }).code = "auth/timeout";
      reject(timeoutError);
    }, NATIVE_AUTH_TIMEOUT_MS);
  });

  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

// Helper function that wraps both analytics and crashlytics logging
function logCustomKeyAndUserProperty(params: { key: string; value: string }) {
  const { key, value } = params;
  crashlytics.setCustomKey(key, value);
  analytics.setUserProperty(key, value);
}

// Helper function to set Crashlytics and Analytics user properties
function setUserAnalyticsAndCrashlytics(firebaseUser: any, user: any) {
  logCustomKeyAndUserProperty({
    key: "accountCreatedAt",
    value: firebaseUser.metadata.creationTime || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "age",
    value: user.dateOfBirth
      ? String(CommonFunctions.calculateAge(user.dateOfBirth))
      : "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "appBuildNumber",
    value: process.env.REACT_APP_BUILD_NUMBER || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "appVersion",
    value: APP_VERSION || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "appVersionMajor",
    value: APP_VERSION ? APP_VERSION.split(".")[0] : "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "appVersionMinor",
    value: APP_VERSION ? APP_VERSION.split(".")[1] : "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "appVersionPatch",
    value: APP_VERSION ? APP_VERSION.split(".")[2] : "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "appVersionPrerelease",
    value:
      APP_VERSION && APP_VERSION.includes("-")
        ? APP_VERSION.split("-")[1]
        : "stable",
  });
  logCustomKeyAndUserProperty({
    key: "colorDepth",
    value: String(window.screen.colorDepth),
  });
  logCustomKeyAndUserProperty({
    key: "colorScheme",
    value:
      window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light",
  });
  logCustomKeyAndUserProperty({
    key: "connectionDownlink",
    value: (navigator as any).connection?.downlink
      ? String((navigator as any).connection?.downlink)
      : "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "connectionRtt",
    value: (navigator as any).connection?.rtt
      ? String((navigator as any).connection?.rtt)
      : "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "connectionSaveData",
    value: (navigator as any).connection?.saveData ? "true" : "false",
  });
  logCustomKeyAndUserProperty({
    key: "connectionType",
    value: (navigator as any).connection?.effectiveType || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "cookiesEnabled",
    value: navigator.cookieEnabled ? "true" : "false",
  });
  logCustomKeyAndUserProperty({
    key: "dateOfBirth",
    value: user.dateOfBirth || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "deviceMemory",
    value: (navigator as any).deviceMemory
      ? String((navigator as any).deviceMemory)
      : "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "devicePixelRatio",
    value: String(window.devicePixelRatio),
  });
  logCustomKeyAndUserProperty({
    key: "doNotTrack",
    value: navigator.doNotTrack || "unknown",
  });
  logCustomKeyAndUserProperty({ key: "email", value: user.email || "unknown" });
  logCustomKeyAndUserProperty({
    key: "emailVerified",
    value: firebaseUser.emailVerified ? "true" : "false",
  });
  logCustomKeyAndUserProperty({
    key: "environment",
    value: process.env.NODE_ENV || "not_set",
  });
  logCustomKeyAndUserProperty({
    key: "gender",
    value: user.gender || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "hardwareConcurrency",
    value: navigator.hardwareConcurrency
      ? String(navigator.hardwareConcurrency)
      : "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "hasCompletedOnboarding",
    value: user.hasCompletedOnboarding ? "true" : "false",
  });
  logCustomKeyAndUserProperty({
    key: "isAnonymous",
    value: firebaseUser.isAnonymous ? "true" : "false",
  });
  logCustomKeyAndUserProperty({
    key: "isOnline",
    value: navigator.onLine ? "true" : "false",
  });
  logCustomKeyAndUserProperty({
    key: "language",
    value: navigator.language || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "lastSignInTime",
    value: firebaseUser.metadata.lastSignInTime || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "linkedProviderCount",
    value: Array.isArray(firebaseUser.providerData)
      ? String(firebaseUser.providerData.length)
      : "0",
  });
  logCustomKeyAndUserProperty({
    key: "linkedProviders",
    value: Array.isArray(user.providerData)
      ? user.providerData.map((p: any) => p.providerId).join(",")
      : "none",
  });
  logCustomKeyAndUserProperty({
    key: "locale",
    value: navigator.language || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "location",
    value: user.location || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "maxTouchPoints",
    value: navigator.maxTouchPoints
      ? String(navigator.maxTouchPoints)
      : "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "platform",
    value: Capacitor.getPlatform(),
  });
  logCustomKeyAndUserProperty({
    key: "platformDetails",
    value: navigator.platform || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "reducedMotion",
    value:
      window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "reduce"
        : "no-preference",
  });
  logCustomKeyAndUserProperty({
    key: "screenHeight",
    value: String(window.screen.height),
  });
  logCustomKeyAndUserProperty({
    key: "screenOrientation",
    value: (screen.orientation || {}).type || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "screenResolution",
    value: `${window.screen.width}x${window.screen.height}`,
  });
  logCustomKeyAndUserProperty({
    key: "screenWidth",
    value: String(window.screen.width),
  });
  logCustomKeyAndUserProperty({
    key: "timestamp",
    value: new Date().toISOString(),
  });
  logCustomKeyAndUserProperty({
    key: "timezone",
    value: Intl.DateTimeFormat().resolvedOptions().timeZone || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "timezoneOffset",
    value: String(new Date().getTimezoneOffset()),
  });
  logCustomKeyAndUserProperty({
    key: "userAuthenticated",
    value: `${firebaseUser.uid}`,
  });
  logCustomKeyAndUserProperty({
    key: "userAgent",
    value: navigator.userAgent || "unknown",
  });
  logCustomKeyAndUserProperty({ key: "userId", value: `${firebaseUser.uid}` });
  logCustomKeyAndUserProperty({
    key: "username",
    value: user.username || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "vendor",
    value: navigator.vendor || "unknown",
  });
  logCustomKeyAndUserProperty({
    key: "windowInnerHeight",
    value: String(window.innerHeight),
  });
  logCustomKeyAndUserProperty({
    key: "windowInnerWidth",
    value: String(window.innerWidth),
  });
  logCustomKeyAndUserProperty({
    key: "windowOuterHeight",
    value: String(window.outerHeight),
  });
  logCustomKeyAndUserProperty({
    key: "windowOuterWidth",
    value: String(window.outerWidth),
  });

  crashlytics.setUserId(`${firebaseUser.uid}`);
  analytics.setUserId(`${firebaseUser.uid}`);
  crashlytics.log(`User Authenticated: ${firebaseUser.uid}`);
  analytics.logEvent("auth_state_changed", {
    timestamp: new Date().toISOString(),
    userId: firebaseUser.uid,
    username: user.username || "unknown",
    email: user.email || "unknown",
    hasCompletedOnboarding: user.hasCompletedOnboarding ? "true" : "false",
    emailVerified: firebaseUser.emailVerified ? "true" : "false",
    platform: Capacitor.getPlatform(),
    gender: user.gender || "unknown",
    age: user.dateOfBirth
      ? String(CommonFunctions.calculateAge(user.dateOfBirth))
      : "unknown",
    location: user.location || "unknown",
  });
}

interface AuthContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  signupWithGoogle: () => Promise<void>;
  signupWithApple: () => Promise<void>;
  signup: (
    email: string,
    password: string,
    profileImageUrl?: string,
  ) => Promise<void>;
  logout: () => void;
  deleteAccount: () => Promise<void>;
  deleteAuthUser: () => Promise<void>;
  authFirebaseUser: React.RefObject<FirebaseUser | null>;
  resendVerificationEmail: () => Promise<void>;
  isEmailVerified: () => boolean;
  getUserById: (id: string) => Promise<User | undefined>;
  getUsersByIds: (ids: string[]) => Promise<User[]>;
  getAllUsers: () => Promise<User[]>;
  searchUsers: (
    searchQuery: string,
    lastDoc?: any,
    limit?: number,
  ) => Promise<{ users: User[]; lastDoc: any; hasMore: boolean }>;
  updateUser: (updatedUserData: Partial<User>) => Promise<void>;
  markOnboardingComplete: () => Promise<void>;
  // Follow actions
  requestFollow: (targetUserId: string) => Promise<void>;
  cancelFollowRequest: (targetUserId: string) => Promise<void>;
  acceptFollowRequest: (requesterId: string) => Promise<void>;
  declineFollowRequest: (requesterId: string) => Promise<void>;
  unfollowUser: (targetUserId: string) => Promise<void>;
  establishMutualFollow: (targetUserId: string) => Promise<void>;
  removeFollower: (followerId: string) => Promise<void>;
  // Account linking
  linkGoogleAccount: () => Promise<void>;
  linkAppleAccount: () => Promise<void>;
  getLinkedProviders: () => UserInfo[];
  unlinkProvider: (providerId: string) => Promise<void>;
  // Private relay helpers
  isApplePrivateRelay: (email: string | null | undefined) => boolean;
  shouldShowPrivateRelayBanner: boolean;
  dismissPrivateRelayBanner: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // State for sign up confirmation modal
  const [showSignupConfirm, setShowSignupConfirm] = useState(false);
  const [signupConfirmMessage, setSignupConfirmMessage] = useState("");
  // Track which provider to use for direct signup
  const [signupProvider, setSignupProvider] = useState<"google" | "apple">(
    "google",
  );
  // State for direct signup loading and error
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [signupError, setSignupError] = useState("");
  // Terms and Conditions checkbox state for ConfirmModal
  const [termsChecked, setTermsChecked] = useState(false);

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [shouldShowPrivateRelayBanner, setShouldShowPrivateRelayBanner] =
    useState(false);
  const authFirebaseUser = useRef<FirebaseUser | null>(null);
  // Ref to track if component is mounted
  const isMounted = useRef(false);

  // --- SocialLogin Initialization Guard ---
  // Ensures SocialLogin is always initialized before any login/signup on native
  const socialLoginInitialized = useRef(false);
  const ensureSocialLoginInitialized = async () => {
    if (!Capacitor.isNativePlatform()) return;
    if (socialLoginInitialized.current) return;
    const platform = Capacitor.getPlatform();
    const googleConfig = {
      webClientId:
        "85898570958-8h10fsbprdv22vq2orj0s5c6qvmog8o8.apps.googleusercontent.com",
      iOSClientId:
        "85898570958-46v9uh897p2i61utqtfidc91ehcnj1e7.apps.googleusercontent.com",
      iOSServerClientId:
        "85898570958-8h10fsbprdv22vq2orj0s5c6qvmog8o8.apps.googleusercontent.com",
    };
    if (platform === "ios") {
      await SocialLogin.initialize({
        google: googleConfig,
        apple: {}, // Provide Apple config only on iOS
      });
    } else {
      await SocialLogin.initialize({
        google: googleConfig,
      });
    }
    socialLoginInitialized.current = true;
  };

  // Reset checkbox when modal opens/closes
  useEffect(() => {
    if (!showSignupConfirm) setTermsChecked(false);
  }, [showSignupConfirm]);

  // Initialize SocialLogin on mount for native platforms
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      ensureSocialLoginInitialized();
    }
  }, []);

  const startSession = async (launched?: boolean) => {
    // App has come to foreground
    try {
      const userDocRef = doc(db, "users", currentUser.id);
      let now = new Date().toISOString();
      let launchNumber = undefined;
      if (launched) {
        const userDocSnap = await getDoc(userDocRef);
        launchNumber = userDocSnap.exists()
          ? (userDocSnap.data().appSessionLaunchNumber || 0) + 1
          : 1;
        logCustomKeyAndUserProperty({
          key: "appSessionLaunchNumber",
          value: String(launchNumber),
        });
        await updateDoc(userDocRef, {
          isOnline: true,
          appSessionLaunchNumber: launchNumber,
          appSessionStartTime: now,
          lastActiveAt: now,
        });
      } else {
        await updateDoc(userDocRef, {
          isOnline: true,
          appSessionStartTime: now,
          lastActiveAt: now,
        });
      }
      logCustomKeyAndUserProperty({ key: "appSessionStartTime", value: now });
      crashlytics.log(
        `App resumed:${launchNumber ? ` appSessionLaunchNumber=${launchNumber}` : ""} appSessionStartTime=${now}`,
      );
    } catch (err) {
      crashlytics.logError(
        `Error setting isOnline true on app resume: ${JSON.stringify(err)}`,
        "AuthContext.tsx",
        err,
      );
    }
  };

  const endSession = async () => {
    // App is going to background
    try {
      const userDocRef = doc(db, "users", currentUser.id);
      // get the current set total time spent and add to cumulative
      const userDocSnap = await getDoc(userDocRef);
      let now = new Date().toISOString();
      let sessionNumber = userDocSnap.exists()
        ? (userDocSnap.data().appSessionNumber || 0) + 1
        : 1;
      let sessionStartTime =
        userDocSnap.exists() && userDocSnap.data().appSessionStartTime;
      const sessionStart = sessionStartTime
        ? new Date(sessionStartTime).getTime()
        : Date.now();
      const sessionDuration = Date.now() - sessionStart;
      let oldTotalTimeSpent = userDocSnap.exists()
        ? userDocSnap.data().appSessionTotalTimeSpent || 0
        : 0;
      const newTotalTimeSpent = oldTotalTimeSpent + sessionDuration;
      const humanReadableDuration =
        CommonFunctions.formatDuration(sessionDuration);
      const humanReadableTotalDuration =
        CommonFunctions.formatDuration(newTotalTimeSpent);
      logCustomKeyAndUserProperty({
        key: "appSessionNumber",
        value: String(sessionNumber),
      });
      logCustomKeyAndUserProperty({ key: "appSessionEndTime", value: now });
      logCustomKeyAndUserProperty({
        key: "appSessionSpent",
        value: sessionDuration.toString(),
      });
      logCustomKeyAndUserProperty({
        key: "appSessionTotalSpent",
        value: newTotalTimeSpent.toString(),
      });
      logCustomKeyAndUserProperty({
        key: "appSessionHRSpent",
        value: humanReadableDuration,
      });
      logCustomKeyAndUserProperty({
        key: "appSessionHRTotalSpent",
        value: humanReadableTotalDuration,
      });
      await updateDoc(userDocRef, {
        isOnline: false,
        lastActiveAt: now,
        appSessionNumber: sessionNumber,
        appSessionEndTime: now,
        appSessionSpent: sessionDuration,
        appSessionTotalSpent: newTotalTimeSpent,
        appSessionHRSpent: humanReadableDuration,
        appSessionHRTotalSpent: humanReadableTotalDuration,
      });
      crashlytics.log(
        `App paused: sessionNumber=${sessionNumber} sessionDuration=${sessionDuration}ms (${humanReadableDuration}), totalTimeSpent=${newTotalTimeSpent}ms (${humanReadableTotalDuration})`,
      );
    } catch (err) {
      crashlytics.logError(
        `Error setting isOnline false on tab close: ${JSON.stringify(err)}`,
        "AuthContext.tsx",
        err,
      );
    }
  };

  // For native platforms, use Capacitor AppState events
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!currentUser || !db || !isAuthenticated) return;
    if (isMounted.current === true) {
      return;
    }
    isMounted.current = true;
    crashlytics.log(
      `Starting session on native (${Capacitor.getPlatform()}): userId=${currentUser.id} at ${new Date().toISOString()}`,
    );
    startSession(true);
    // These return Promises, but we don't care about the result until cleanup
    const statePromise = CapacitorApp.addListener(
      "appStateChange",
      async (state) => {
        if (state.isActive) {
          startSession();
        } else {
          endSession();
        }
      },
    );
    return () => {
      statePromise
        .then((h) => h.remove())
        .catch((e) =>
          crashlytics.logError(
            "Cleanup error for appStateChange",
            "AuthContext.tsx",
            e,
          ),
        );
      isMounted.current = false;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    // Guard against double mount in React StrictMode
    const isEffectActive = { current: true } as { current: boolean };
    if ((useRef as any)._authEffectInit) {
      return undefined;
    }
    (useRef as any)._authEffectInit = true;

    if (!auth || !db) {
      setIsLoading(false);
      return;
    }

    let userDocUnsubscribe: (() => void) | undefined;

    const authUnsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser: FirebaseUser | null) => {
        if (!db) return;
        // Unsubscribe from any previous user document listener
        if (userDocUnsubscribe) {
          userDocUnsubscribe();
        }
        if (firebaseUser) {
          authFirebaseUser.current = firebaseUser;
          const userDocRef = doc(db, "users", firebaseUser.uid);

          try {
            const initialDocSnap = await getDoc(userDocRef);
            if (isEffectActive.current && initialDocSnap.exists()) {
              let user = {
                id: initialDocSnap.id,
                ...initialDocSnap.data(),
              } as User;
              setCurrentUser(user);
              setIsAuthenticated(true);

              // Debug: Log private relay banner conditions
              const privateRelayEmail = isApplePrivateRelay(firebaseUser.email);
              const linkedProviderIds = Array.isArray(firebaseUser.providerData)
                ? firebaseUser.providerData.map((p) => p.providerId)
                : [];
              const shouldSuggest = shouldSuggestAccountLinking(
                firebaseUser.email,
                linkedProviderIds,
              );

              // Check if should show private relay banner (only after onboarding)
              if (
                user.hasCompletedOnboarding &&
                privateRelayEmail &&
                !user.dismissedPrivateRelayBanner &&
                shouldSuggest
              ) {
                setShouldShowPrivateRelayBanner(true);
              } else {
                setShouldShowPrivateRelayBanner(false);
              }

              if (isEffectActive.current) {
                setUserAnalyticsAndCrashlytics(firebaseUser, user);
                setIsLoading(false);
              }

              // Check for pending notification navigation after auth is restored
              try {
                const pendingNav = sessionStorage.getItem(
                  "pendingNotificationNavigation",
                );
                if (pendingNav) {
                  const navData = JSON.parse(pendingNav);
                  // Only navigate if the stored intent is recent (within 10 seconds)
                  if (Date.now() - navData.timestamp < 10000) {
                    sessionStorage.removeItem("pendingNotificationNavigation");
                    // Give a small delay for the app to fully load
                    setTimeout(() => {
                      window.history.pushState({}, "", navData.path);
                      window.dispatchEvent(new PopStateEvent("popstate"));
                      window.dispatchEvent(
                        new CustomEvent("notification-navigate", {
                          detail: {
                            path: navData.path,
                            source: "pending-notification",
                          },
                        }),
                      );
                    }, 500);
                  } else {
                    // Clear old navigation intent
                    sessionStorage.removeItem("pendingNotificationNavigation");
                  }
                }
              } catch (e) {
                console.warn(
                  "Failed to check pending notification navigation:",
                  e,
                );
              }

              // Check if user signed up with email/password and hasn't verified yet
              const hasPasswordProvider = firebaseUser.providerData.some(
                (provider) => provider.providerId === "password",
              );
            } else {
              setCurrentUser(null);
              setIsLoading(false);
            }

            // Set up real-time listener for future updates
            userDocUnsubscribe = onSnapshot(userDocRef, {
              next: (docSnapshot) => {
                // Check if user is still authenticated before updating state
                if (isEffectActive.current && auth.currentUser && docSnapshot.exists()) {
                  const user = {
                    id: docSnapshot.id,
                    ...docSnapshot.data(),
                  } as User;
                  setCurrentUser(user);

                  // Debug: Log private relay banner conditions (also check on snapshot updates)
                  const privateRelayEmail = isApplePrivateRelay(
                    auth.currentUser.email,
                  );
                  const linkedProviderIds = Array.isArray(
                    auth.currentUser.providerData,
                  )
                    ? auth.currentUser.providerData.map(
                      (p: { providerId: any }) => p.providerId,
                    )
                    : [];
                  const shouldSuggest = shouldSuggestAccountLinking(
                    auth.currentUser.email,
                    linkedProviderIds,
                  );

                  // Check if should show private relay banner (only after onboarding)
                  if (
                    user.hasCompletedOnboarding &&
                    privateRelayEmail &&
                    !user.dismissedPrivateRelayBanner &&
                    shouldSuggest
                  ) {
                    setShouldShowPrivateRelayBanner(true);
                  }
                }

                if (isEffectActive.current) {
                  setIsLoading(false);
                }
              },
              error: (error) => {
                // Only log error if user is still authenticated (not a logout scenario)
                if (isEffectActive.current && auth.currentUser) {
                  crashlytics.logError(
                    `Error in onSnapshot listener: ${JSON.stringify(error?.message)}`,
                    "AuthContext.tsx",
                    error,
                  );
                }
                if (isEffectActive.current) {
                  setCurrentUser(null);
                  setIsLoading(false);
                }
              },
            });
          } catch (error) {
            crashlytics.logError(
              `Error in onSnapshot listener: ${JSON.stringify(error)}`,
              "AuthContext.tsx",
              error,
            );
            if (isEffectActive.current) {
              setCurrentUser(null);
              setIsLoading(false);
            }
          }
        } else {
          if (isEffectActive.current) {
            setCurrentUser(null);
            setIsAuthenticated(false);
            setShouldShowPrivateRelayBanner(false);
            setIsLoading(false);
          }
        }
      },
    );

    // Workaround for iOS capacitor: manually check current user state
    // onAuthStateChanged sometimes doesn't fire on capacitor:// scheme
    setTimeout(() => {
      const currentAuthUser = auth.currentUser;
      if (!currentAuthUser && isLoading) {
        setCurrentUser(null);
        setIsLoading(false);
      }
    }, 2000);

    // Cleanup function
    return () => {
      authUnsubscribe();
      if (userDocUnsubscribe) {
        userDocUnsubscribe();
      }
      isEffectActive.current = false;
      (useRef as any)._authEffectInit = false;
    };
  }, []);

  const login = async (email: string, password: string) => {
    if (!db) return;
    try {
      if (!auth) {
        throw new Error(
          "Firebase Auth is not initialized. Please refresh the page and try again.",
        );
      }

      // Add explicit persistence before sign in (only for web, native already has it)
      if (!Capacitor.isNativePlatform()) {
        await setPersistence(auth, browserLocalPersistence);
      }

      // Create a timeout promise
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error("auth/timeout"));
        }, 10000); // 10 second timeout
      });

      // Race between sign in and timeout
      const result = (await Promise.race([
        signInWithEmailAndPassword(auth, email, password),
        timeoutPromise,
      ])) as any;

      // Update last login time with metadata
      if (result?.user) {
        const loginMetadata = await getLoginMetadata("email");
        const userDocRef = doc(db, "users", result.user.uid);
        const lastKnownCountry = loginMetadata.deviceInfo?.platform
          ? null
          : (loginMetadata.deviceInfo?.timezone?.split("/")[0] ?? null);
        updateDoc(
          userDocRef,
          CommonFunctions.removeUndefined({
            lastLoginAt: new Date().toISOString(),
            isOnline: true,
            lastLoginMetadata: loginMetadata,
            lastKnownCountry,
            lastKnownTimezone: loginMetadata.deviceInfo?.timezone,
          }),
        );
      }

      // Log login event to Analytics
      analytics.logEvent("wii_user_login", {
        method: "email",
        timestamp: new Date().toISOString(),
      });
      crashlytics.log(`User Logged In: ${email}`);
      authFirebaseUser.current = result?.user || null;
      // onAuthStateChanged will fire automatically and update the state
      // The LoginPage useEffect will handle navigation when isAuthenticated becomes true
    } catch (authError: any) {
      // Provide user-friendly error messages
      if (
        authError.message === "auth/timeout" ||
        authError.code === "auth/timeout"
      ) {
        throw new Error(
          "Login is taking too long. Please check your connection and try again.",
        );
      } else if (authError.message?.includes("not initialized")) {
        throw authError;
      } else if (authError.code === "auth/network-request-failed") {
        throw new Error(
          "Network error. Please check your internet connection.",
        );
      } else if (
        authError.code === "auth/wrong-password" ||
        authError.code === "auth/invalid-credential"
      ) {
        throw new Error("Invalid email or password");
      } else if (authError.code === "auth/user-not-found") {
        throw new Error("No account found with this email");
      } else if (authError.code === "auth/too-many-requests") {
        throw new Error(
          "Too many failed login attempts. Please try again later",
        );
      } else {
        throw authError;
      }
    }
  };

  const loginWithGoogle = async () => {
    if (!db) return;
    try {
      let firebaseUser;

      if (Capacitor.isNativePlatform()) {
        await ensureSocialLoginInitialized();

        // Force logout from Google to ensure account picker appears on iOS
        try {
          await SocialLogin.logout({ provider: "google" });
        } catch (e) {
          // Ignore error if not logged in
        }

        const res = await SocialLogin.login({
          provider: "google",
          options: {
            scopes: ["email", "profile"],
            forceRefreshToken: true,
          },
        });
        if (
          !res ||
          !res.result ||
          (res.result as any).responseType !== "online" ||
          !(res.result as any).idToken
        ) {
          throw new Error("Google sign-in failed or was cancelled.");
        }
        const credential = GoogleAuthProvider.credential(
          (res.result as any).idToken,
        );
        const userCredential = await withNativeAuthTimeout(
          signInWithCredential(auth, credential),
        );
        firebaseUser = userCredential.user;
      } else {
        // Web: Use popup
        if (!auth) {
          crashlytics.logError(
            "Firebase Auth not initialized",
            "AuthContext.tsx",
            new Error("Auth not initialized"),
          );
          throw new Error(
            "Firebase Auth is not initialized. Please refresh the page.",
          );
        }

        // Ensure auth is fully initialized before proceeding
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Check if running in development
        const isDevelopment =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";

        if (isDevelopment) {
          throw new Error(
            "Google Sign-In is not available in development mode. Please use email/password sign-in, or test on production/mobile.",
          );
        }

        const provider = new GoogleAuthProvider();
        provider.addScope("email");
        provider.addScope("profile");

        // Set persistence before sign in
        await setPersistence(auth, browserLocalPersistence);

        // Try with explicit parameters and catch to see internal error
        try {
          const result = await signInWithPopup(
            auth,
            provider,
            browserPopupRedirectResolver,
          );
          firebaseUser = result.user;
        } catch (popupError: any) {
          crashlytics.logError(
            `Popup error details: code=${popupError?.code}, message=${popupError?.message}, customData=${JSON.stringify(popupError?.customData)}, name=${popupError?.name}, keys=${Object.keys(popupError).join(",")}`,
            "AuthContext.tsx",
            popupError,
          );
          throw popupError;
        }
      }

      const userDocRef = doc(db, "users", firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        // Account doesn't exist - delete the Firebase Auth user that was just created
        try {
          await firebaseUser.delete();
        } catch (deleteError) {
          crashlytics.logError(
            `Error deleting Firebase Auth user: ${JSON.stringify(deleteError)}`,
            "AuthContext.tsx",
            deleteError,
          );
          await signOut(auth);
        }
        setSignupConfirmMessage(
          "No account found with this Google account. Please sign up first to accept our Terms and Conditions.",
        );
        setSignupProvider("google");
        setShowSignupConfirm(true);
        return;
      } else {
        // Existing user - collect login metadata and update
        const existingUser = {
          id: userDoc.id,
          ...userDoc.data(),
        } as User;

        // Native auth callbacks can be delayed; set local auth state immediately
        // so login redirect can proceed as soon as Google sign-in succeeds.
        setCurrentUser(existingUser);
        setIsAuthenticated(true);
        setUserAnalyticsAndCrashlytics(firebaseUser, existingUser);
        setIsLoading(false);

        const loginMetadata = await getLoginMetadata("google");
        const lastKnownCountry = loginMetadata.deviceInfo?.platform
          ? null
          : (loginMetadata.deviceInfo?.timezone?.split("/")[0] ?? null);
        updateDoc(
          userDocRef,
          CommonFunctions.removeUndefined({
            lastLoginAt: new Date().toISOString(),
            isOnline: true,
            lastLoginMetadata: loginMetadata,
            lastKnownCountry,
            lastKnownTimezone: loginMetadata.deviceInfo?.timezone,
          }),
        );
        // Log login event
        analytics.logEvent("wii_user_login", {
          method: "google",
          timestamp: new Date().toISOString(),
        });
        crashlytics.log(
          `User Logged In with Google: ${firebaseUser.email} (${firebaseUser.uid})`,
        );
        authFirebaseUser.current = firebaseUser;
      }
      // For existing users, onAuthStateChanged will automatically fetch and set the user data
    } catch (error: any) {
      crashlytics.logError(
        `Error during Google sign-in errorCode=${error?.code} Error: ${JSON.stringify(error?.message)}`,
        "AuthContext.tsx",
        error,
      );
      // Handle specific Firebase errors
      if (error.code === "auth/popup-closed-by-user") {
        throw new Error("Sign-in cancelled. Please try again.");
      } else if (error.code === "auth/popup-blocked") {
        throw new Error(
          "Pop-up blocked by browser. Please allow pop-ups and try again.",
        );
      } else if (error.code === "auth/cancelled-popup-request") {
        throw new Error("Sign-in cancelled. Please try again.");
      } else if (
        error.code === "auth/network-request-failed" ||
        error.code === "auth/timeout" ||
        error.message === "auth/timeout"
      ) {
        throw new Error(
          "Network error. Please check your connection and try again.",
        );
      } else if (error.code === "auth/unauthorized-domain") {
        throw new Error(
          "This domain is not authorized for Google sign-in. Please contact support.",
        );
      } else if (error.message?.includes("not initialized")) {
        throw new Error(
          "Authentication not ready. Please refresh the page and try again.",
        );
      }

      throw error;
    }
  };

  const loginWithApple = async () => {
    if (!db) return;
    try {
      let firebaseUser;

      if (Capacitor.isNativePlatform()) {
        await ensureSocialLoginInitialized();
        let retryAfterReset = false;
        try {
          const res = await SocialLogin.login({
            provider: "apple",
            options: {
              scopes: ["email", "name"],
            },
          });
          if (!res || !res.result || !(res.result as any).idToken) {
            throw new Error("Apple sign-in failed or was cancelled.");
          }
          const provider = new OAuthProvider("apple.com");
          const credential = provider.credential({
            idToken: (res.result as any).idToken,
          });
          const userCredential = await withNativeAuthTimeout(
            signInWithCredential(auth, credential),
          );
          firebaseUser = userCredential.user;
        } catch (pluginError: any) {
          crashlytics.logError(
            `Error during Apple sign-in plugin errorCode=${pluginError?.code} Error: ${JSON.stringify(pluginError?.message)}`,
            "AuthContext.tsx",
            pluginError,
          );

          // Handle "Last call is not null" - plugin state stuck after cancellation
          // This is a plugin bug - the only way to clear it is to reinitialize
          if (
            pluginError.message?.includes("Last call is not null") &&
            !retryAfterReset
          ) {
            // Attempt to reinitialize the plugin to clear the stuck state
            try {
              await ensureSocialLoginInitialized();

              // Add a small delay to let the plugin fully reset
              await new Promise((resolve) => setTimeout(resolve, 300));

              // Plugin reset successful, now retry the login automatically
              retryAfterReset = true;
              const res = await SocialLogin.login({
                provider: "apple",
                options: {
                  scopes: ["email", "name"],
                },
              });

              if (!res || !res.result || !(res.result as any).idToken) {
                throw new Error("Apple sign-in failed or was cancelled.");
              }

              // Get Firebase credential from Apple ID token
              const provider = new OAuthProvider("apple.com");
              // Note: The plugin doesn't provide nonce for iOS native flow
              const credential = provider.credential({
                idToken: (res.result as any).idToken,
              });

              // Sign in to Firebase with the credential
              const userCredential = await withNativeAuthTimeout(
                signInWithCredential(auth, credential),
              );
              firebaseUser = userCredential.user;
            } catch (resetError: any) {
              crashlytics.logError(
                `Error during Apple sign-in reset errorCode=${resetError?.code} Error: ${JSON.stringify(resetError?.message)}`,
                "AuthContext.tsx",
                resetError,
              );

              // Check if this is a Firebase state error
              if (
                resetError.message?.includes("missing initial state") ||
                resetError.code === "auth/internal-error"
              ) {
                // This happens when Firebase auth state is corrupted - ask user to restart app
                throw new Error(
                  "Please close and reopen the app to sign in with Apple.",
                );
              }

              throw new Error(
                "Unable to sign in with Apple. Please try again or restart the app.",
              );
            }
          } else {
            // If user cancelled, throw a user-friendly error
            if (
              pluginError.message?.includes("cancel") ||
              pluginError.message?.includes("Cancel")
            ) {
              throw new Error("Sign-in cancelled.");
            }

            throw pluginError;
          }
        }
      } else {
        // Web: Use popup
        if (!auth) {
          throw new Error(
            "Firebase Auth is not initialized. Please refresh the page.",
          );
        }

        // Ensure auth is fully initialized before proceeding
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Check if running in development
        const isDevelopment =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";

        if (isDevelopment) {
          throw new Error(
            "Apple Sign-In is not available in development mode. Please use email/password sign-in, or test on production/mobile.",
          );
        }

        const provider = new OAuthProvider("apple.com");
        provider.addScope("email");
        provider.addScope("name");

        // Set persistence before sign in
        await setPersistence(auth, browserLocalPersistence);

        const result = await signInWithPopup(
          auth,
          provider,
          browserPopupRedirectResolver,
        );
        firebaseUser = result.user;
      }

      const userDocRef = doc(db, "users", firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        // Account doesn't exist - delete the Firebase Auth user that was just created
        // This prevents orphaned auth users without Firestore profiles
        try {
          await firebaseUser.delete();
        } catch (deleteError) {
          crashlytics.logError(
            `Error deleting Firebase Auth user: ${JSON.stringify(deleteError)}`,
            "AuthContext.tsx",
            deleteError,
          );
          // If delete fails, at least sign them out
          await signOut(auth);
        }
        setSignupConfirmMessage(
          "No account found with this Apple account. Please sign up first to accept our Terms and Conditions.",
        );
        setSignupProvider("apple");
        setShowSignupConfirm(true);
        return;
      } else {
        // Existing user - collect login metadata and update
        const userData = userDoc.data() as User;
        const loginMetadata = await getLoginMetadata("apple");
        const lastKnownCountry = loginMetadata.deviceInfo?.platform
          ? null
          : (loginMetadata.deviceInfo?.timezone?.split("/")[0] ?? null);
        updateDoc(
          userDocRef,
          CommonFunctions.removeUndefined({
            lastLoginAt: new Date().toISOString(),
            isOnline: true,
            lastLoginMetadata: loginMetadata,
            lastKnownCountry,
            lastKnownTimezone: loginMetadata.deviceInfo?.timezone,
          }),
        );

        // Check if should show private relay banner for existing users (only after onboarding)
        if (
          userData.hasCompletedOnboarding &&
          isApplePrivateRelay(firebaseUser.email) &&
          !userData.dismissedPrivateRelayBanner &&
          shouldSuggestAccountLinking(
            firebaseUser.email,
            firebaseUser.providerData.map((p) => p.providerId),
          )
        ) {
          setShouldShowPrivateRelayBanner(true);
        }

        analytics.logEvent("wii_user_login", {
          method: "apple",
          timestamp: new Date().toISOString(),
        });
        crashlytics.log(
          `User Logged In with Apple: ${firebaseUser.email} (${firebaseUser.uid})`,
        );
        authFirebaseUser.current = firebaseUser;
      }
    } catch (error: any) {
      crashlytics.logError(
        `Error during Apple sign-in errorCode=${error?.code} Error: ${JSON.stringify(error?.message)}`,
        "AuthContext.tsx",
        error,
      );

      // Handle specific Firebase errors
      if (error.code === "auth/popup-closed-by-user") {
        throw new Error("Sign-in cancelled. Please try again.");
      } else if (error.code === "auth/popup-blocked") {
        throw new Error(
          "Pop-up blocked by browser. Please allow pop-ups and try again.",
        );
      } else if (error.code === "auth/cancelled-popup-request") {
        throw new Error("Sign-in cancelled. Please try again.");
      } else if (
        error.code === "auth/network-request-failed" ||
        error.code === "auth/timeout" ||
        error.message === "auth/timeout"
      ) {
        throw new Error(
          "Network error. Please check your connection and try again.",
        );
      } else if (error.code === "auth/unauthorized-domain") {
        throw new Error(
          "This domain is not authorized for Apple sign-in. Please contact support.",
        );
      } else if (error.message?.includes("not initialized")) {
        throw new Error(
          "Authentication not ready. Please refresh the page and try again.",
        );
      }

      throw error;
    }
  };

  const signupWithGoogle = async () => {
    if (!db) return;
    try {
      let firebaseUser;

      if (Capacitor.isNativePlatform()) {
        await ensureSocialLoginInitialized();

        // Force logout from Google to ensure account picker appears on iOS
        try {
          await SocialLogin.logout({ provider: "google" });
        } catch (e) {
          // Ignore error if not logged in
        }

        const res = await SocialLogin.login({
          provider: "google",
          options: {
            scopes: ["email", "profile"],
            forceRefreshToken: true,
          },
        });
        if (
          !res ||
          !res.result ||
          (res.result as any).responseType !== "online" ||
          !(res.result as any).idToken
        ) {
          throw new Error("Google sign-in failed or was cancelled.");
        }
        const credential = GoogleAuthProvider.credential(
          (res.result as any).idToken,
        );
        const userCredential = await withNativeAuthTimeout(
          signInWithCredential(auth, credential),
        );
        firebaseUser = userCredential.user;
      } else {
        if (!auth) {
          throw new Error(
            "Firebase Auth is not initialized. Please refresh the page.",
          );
        }

        await new Promise((resolve) => setTimeout(resolve, 100));
        const isDevelopment =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";

        if (isDevelopment) {
          throw new Error(
            "Google Sign-In is not available in development mode. Please use email/password sign-in, or test on production/mobile.",
          );
        }

        const provider = new GoogleAuthProvider();
        provider.addScope("email");
        provider.addScope("profile");
        await setPersistence(auth, browserLocalPersistence);

        const result = await signInWithPopup(
          auth,
          provider,
          browserPopupRedirectResolver,
        );
        firebaseUser = result.user;
      }

      const userDocRef = doc(db, "users", firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        // Account already exists - just log them in
        const loginMetadata = await getLoginMetadata("google");
        const lastKnownCountry = loginMetadata.deviceInfo?.platform
          ? null
          : (loginMetadata.deviceInfo?.timezone?.split("/")[0] ?? null);
        updateDoc(userDocRef, {
          lastLoginAt: new Date().toISOString(),
          isOnline: true,
          lastLoginMetadata: loginMetadata,
          lastKnownCountry,
          lastKnownTimezone: loginMetadata.deviceInfo.timezone,
        });
      } else {
        // New user - create profile
        const baseUsername =
          firebaseUser.displayName ||
          firebaseUser.email?.split("@")[0] ||
          "user";
        const username = await generateUniqueUsername(baseUsername);

        const signupMethod: User["signupMethod"] = "google";
        const now = new Date().toISOString();

        const newUser: Omit<User, "id"> = {
          username,
          email: firebaseUser.email || "",
          location: "Singapore",
          isAccountVerifiedForBadge: false,
          profileImageUrl:
            firebaseUser.photoURL ||
            `https://api.dicebear.com/8.x/miniavs/svg?seed=${encodeURIComponent(username)}`,
          isOnline: true,
          followers: [],
          following: [],
          pendingFollowers: [],
          requestedFollowing: [],
          passedActivityIds: [],
          hasCompletedOnboarding: false,
          dateOfBirth: "",
          gender: "undisclosed",
          passions: [],
          bio: "",
          createdAt: now,
          updatedAt: now,
          lastLoginAt: now,
          signupMethod,
        };

        await setDoc(userDocRef, CommonFunctions.removeUndefined(newUser));

        analytics.logEvent("wii_user_signup", {
          method: "google",
          timestamp: new Date().toISOString(),
        });
        crashlytics.log(
          `New User Signed Up with Google: ${firebaseUser.email} (${firebaseUser.uid})`,
        );
        authFirebaseUser.current = firebaseUser;
      }
    } catch (error: any) {
      crashlytics.logError(
        `Error during Google sign-in errorCode=${error?.code} Error: ${JSON.stringify(error?.message)}`,
        "AuthContext.tsx",
        error,
      );

      if (
        error.code === "auth/popup-closed-by-user" ||
        error.code === "auth/cancelled-popup-request"
      ) {
        throw new Error("Sign-up cancelled. Please try again.");
      } else if (
        error.code === "auth/network-request-failed" ||
        error.code === "auth/timeout" ||
        error.message === "auth/timeout"
      ) {
        throw new Error(
          "Network error. Please check your connection and try again.",
        );
      } else if (error.code === "auth/popup-blocked") {
        throw new Error(
          "Pop-up blocked by browser. Please allow pop-ups and try again.",
        );
      } else if (
        error.code === "auth/account-exists-with-different-credential"
      ) {
        throw new Error(
          "An account with this email already exists using a different sign-in method.",
        );
      }

      throw error;
    }
  };

  const signupWithApple = async () => {
    if (!db) return;
    try {
      let firebaseUser;

      if (Capacitor.isNativePlatform()) {
        await ensureSocialLoginInitialized();
        const res = await SocialLogin.login({
          provider: "apple",
          options: {
            scopes: ["email", "name"],
          },
        });
        if (!res || !res.result || !(res.result as any).idToken) {
          throw new Error("Apple sign-in failed or was cancelled.");
        }
        const provider = new OAuthProvider("apple.com");
        const credential = provider.credential({
          idToken: (res.result as any).idToken,
        });
        const userCredential = await withNativeAuthTimeout(
          signInWithCredential(auth, credential),
        );
        firebaseUser = userCredential.user;
      } else {
        if (!auth) {
          throw new Error(
            "Firebase Auth is not initialized. Please refresh the page.",
          );
        }

        await new Promise((resolve) => setTimeout(resolve, 100));
        const isDevelopment =
          window.location.hostname === "localhost" ||
          window.location.hostname === "127.0.0.1";

        if (isDevelopment) {
          throw new Error(
            "Apple Sign-In is not available in development mode. Please use email/password sign-in, or test on production/mobile.",
          );
        }

        const provider = new OAuthProvider("apple.com");
        provider.addScope("email");
        provider.addScope("name");
        await setPersistence(auth, browserLocalPersistence);

        const result = await signInWithPopup(
          auth,
          provider,
          browserPopupRedirectResolver,
        );
        firebaseUser = result.user;
      }

      const userDocRef = doc(db, "users", firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        // Account already exists - just log them in
        const loginMetadata = await getLoginMetadata("apple");
        const lastKnownCountry = loginMetadata.deviceInfo?.platform
          ? null
          : (loginMetadata.deviceInfo?.timezone?.split("/")[0] ?? null);
        updateDoc(userDocRef, {
          lastLoginAt: new Date().toISOString(),
          isOnline: true,
          lastLoginMetadata: loginMetadata,
          lastKnownCountry,
          lastKnownTimezone: loginMetadata.deviceInfo.timezone,
        });
      } else {
        // New user - create profile
        const baseUsername =
          firebaseUser.displayName ||
          firebaseUser.email?.split("@")[0] ||
          "user";
        const username = await generateUniqueUsername(baseUsername);

        const signupMethod: User["signupMethod"] = "apple";
        const now = new Date().toISOString();

        const newUser: Omit<User, "id"> = {
          username,
          email: firebaseUser.email || "",
          location: "Singapore",
          isAccountVerifiedForBadge: false,
          profileImageUrl:
            firebaseUser.photoURL ||
            `https://api.dicebear.com/8.x/miniavs/svg?seed=${encodeURIComponent(username)}`,
          isOnline: true,
          followers: [],
          following: [],
          pendingFollowers: [],
          requestedFollowing: [],
          passedActivityIds: [],
          hasCompletedOnboarding: false,
          dateOfBirth: "",
          gender: "undisclosed",
          passions: [],
          bio: "",
          createdAt: now,
          updatedAt: now,
          lastLoginAt: now,
          signupMethod,
        };

        await setDoc(userDocRef, CommonFunctions.removeUndefined(newUser));

        analytics.logEvent("wii_user_signup", {
          method: "apple",
          timestamp: new Date().toISOString(),
        });
        crashlytics.log(
          `New User Signed Up with Apple: ${firebaseUser.email} (${firebaseUser.uid})`,
        );
        authFirebaseUser.current = firebaseUser;
      }
    } catch (error: any) {
      crashlytics.logError(
        `Error during Apple sign-in errorCode=${error?.code} Error: ${JSON.stringify(error?.message)}`,
        "AuthContext.tsx",
        error,
      );
      if (
        error.code === "auth/popup-closed-by-user" ||
        error.code === "auth/cancelled-popup-request"
      ) {
        throw new Error("Sign-up cancelled. Please try again.");
      } else if (error.code === "auth/popup-blocked") {
        throw new Error(
          "Pop-up blocked by browser. Please allow pop-ups and try again.",
        );
      } else if (
        error.code === "auth/account-exists-with-different-credential"
      ) {
        throw new Error(
          "An account with this email already exists using a different sign-in method.",
        );
      } else if (error.code === "auth/invalid-credential") {
        throw new Error("Invalid Apple credential. Please try again.");
      }

      throw error;
    }
  };

  const checkUsernameExists = async (username: string): Promise<boolean> => {
    if (!db) return false;
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("username", "==", username));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  };

  const generateUniqueUsername = async (
    baseUsername: string,
  ): Promise<string> => {
    let username = baseUsername;

    // Check if username already exists and make it unique if needed
    const usernameExists = await checkUsernameExists(username);
    if (usernameExists) {
      // Append random numbers to make it unique
      let uniqueUsername = username;
      let counter = 1;
      while (await checkUsernameExists(uniqueUsername)) {
        uniqueUsername = `${username}${Math.floor(Math.random() * 10000)}`;
        counter++;
        if (counter > 10) break; // Safety limit
      }
      username = uniqueUsername;
    }

    return username;
  };

  const signup = async (
    email: string,
    password: string,
    profileImageUrl?: string,
  ) => {
    if (!db) return;
    try {
      // Create the Firebase Auth user first so we're authenticated
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );
      const firebaseUser = userCredential.user;

      if (!firebaseUser) {
        throw new Error("User creation failed, no user returned.");
      }

      // Now that we're authenticated, generate unique username
      const baseUsername = email.split("@")[0];
      const username = await generateUniqueUsername(baseUsername);

      const signupMethod: User["signupMethod"] = "email";
      const now = new Date().toISOString();

      // Use provided profileImageUrl, or Google photo, or generate one with Dicebear
      const finalProfileImageUrl =
        profileImageUrl ||
        firebaseUser.photoURL ||
        `https://api.dicebear.com/8.x/miniavs/svg?seed=${encodeURIComponent(username)}`;

      // Create a new user document in Firestore
      const newUser: Omit<User, "id"> = {
        username,
        email,
        location: "Singapore",
        isAccountVerifiedForBadge: false,
        profileImageUrl: finalProfileImageUrl,
        isOnline: true,
        followers: [],
        following: [],
        pendingFollowers: [],
        requestedFollowing: [],
        passedActivityIds: [],
        hasCompletedOnboarding: false,
        dateOfBirth: "",
        gender: "undisclosed",
        passions: [],
        bio: "",
        createdAt: now,
        updatedAt: now,
        lastLoginAt: now,
        signupMethod,
      };

      const userDocRef = doc(db, "users", firebaseUser.uid);
      await setDoc(userDocRef, CommonFunctions.removeUndefined(newUser));

      analytics.logEvent("wii_user_signup", {
        method: "email",
        timestamp: new Date().toISOString(),
      });
      crashlytics.log(
        `New User Signed Up with Email: ${firebaseUser.email} (${firebaseUser.uid})`,
      );
      authFirebaseUser.current = firebaseUser;

      // Send email verification
      try {
        await sendEmailVerification(firebaseUser);
      } catch (verificationError) {
        crashlytics.logError(
          `Error sending verification email: ${JSON.stringify(verificationError)}`,
          "AuthContext.tsx",
          verificationError,
        );
        // Don't block signup if verification email fails to send
      }

      // Don't set currentUser here - let onAuthStateChanged handle it
    } catch (error: any) {
      // Handle specific Firebase Auth errors
      if (error.code === "auth/email-already-in-use") {
        throw new Error(
          "An account with this email already exists. Please sign in instead.",
        );
      } else if (error.code === "auth/weak-password") {
        throw new Error("Password should be at least 6 characters long.");
      } else if (error.code === "auth/invalid-email") {
        throw new Error("Please enter a valid email address.");
      } else if (error.code === "auth/network-request-failed") {
        throw new Error(
          "Network error. Please check your internet connection.",
        );
      } else {
        throw error;
      }
    }
  };

  const logout = async () => {
    try {
      // Set isOnline to false in Firestore before logging out
      if (currentUser && db) {
        try {
          const userDocRef = doc(db, "users", currentUser.id);
          await updateDoc(userDocRef, { isOnline: false });
        } catch (err) {
          crashlytics.logError(
            `Error setting isOnline to false during logout: ${JSON.stringify(err)}`,
            "AuthContext.tsx",
            err,
          );
        }
      }

      // Clear native social login sessions if on native platform
      if (Capacitor.isNativePlatform()) {
        try {
          await SocialLogin.logout({ provider: "google" });
        } catch (e) {
          // Ignore errors if not logged in or provider not used
        }
      }

      // Sign out from Firebase first
      await signOut(auth);

      // Clear current user state to trigger cleanup in other contexts
      setCurrentUser(null);
      setIsLoading(false);

      // PushNotificationManager cleanup
      try {
        await PushNotificationManager.deregister();
      } catch (error) {
        crashlytics.logError(
          `Error deregistering PushNotificationManager: ${JSON.stringify(error)}`,
          "AuthContext.tsx",
          error,
        );
      }

      // Force a page reload to ensure all listeners are properly cleaned up
      // This prevents Firestore listener errors after logout
      setTimeout(() => {
        window.location.href = "/welcome";
      }, 100);
    } catch (error) {
      // Optionally log error
    }
  };

  const deleteAccount = useCallback(async () => {
    if (!db || !currentUser) {
      return;
    }
    try {
      /* This data can be simple moved to 'deleted' collection for record-keeping */
      // Notifications
      const notificationsSnap = await getDocs(
        query(
          collection(db, "notifications"),
          where("userId", "==", currentUser.id),
        ),
      );
      for (const docSnap of notificationsSnap.docs) {
        const deletedNotificationRef = doc(
          db,
          "deleted",
          currentUser.id,
          "notifications",
          docSnap.id,
        );
        await setDoc(deletedNotificationRef, {
          ...docSnap.data(),
          deletedAt: serverTimestamp(),
          deletedBy: auth.currentUser?.uid || currentUser.id,
        });
        try {
          await deleteDoc(docSnap.ref);
        } catch (err) {
          crashlytics.logError(
            `Error deleting notification: ${JSON.stringify({ path: docSnap.ref.path, error: err })}`,
            "AuthContext.tsx",
            err,
          );
        }
      }
      // Stories
      const storiesSnap = await getDocs(
        query(
          collection(db, "stories"),
          where("__name__", "==", currentUser.id),
        ),
      );
      for (const docSnap of storiesSnap.docs) {
        const deletedStoryRef = doc(
          db,
          "deleted",
          currentUser.id,
          "stories",
          docSnap.id,
        );
        await setDoc(deletedStoryRef, {
          ...docSnap.data(),
          deletedAt: serverTimestamp(),
          deletedBy: auth.currentUser?.uid || currentUser.id,
        });
        try {
          await deleteDoc(docSnap.ref);
        } catch (err) {
          crashlytics.logError(
            `Error deleting story: ${JSON.stringify({ path: docSnap.ref.path, error: err })}`,
            "AuthContext.tsx",
            err,
          );
        }
      }
      // AuditLogs
      const auditLogsSnap1 = await getDocs(
        query(
          collection(db, "auditLogs"),
          where("userId", "==", currentUser.id),
        ),
      );
      const auditLogsSnap2 = await getDocs(
        query(
          collection(db, "auditLogs"),
          where("fromUserId", "==", currentUser.id),
        ),
      );
      const auditLogsSnapDocsMap = new Map<string, any>();
      for (const docSnap of auditLogsSnap1.docs) {
        auditLogsSnapDocsMap.set(docSnap.id, docSnap);
      }
      for (const docSnap of auditLogsSnap2.docs) {
        auditLogsSnapDocsMap.set(docSnap.id, docSnap);
      }
      const auditLogsSnap = { docs: Array.from(auditLogsSnapDocsMap.values()) };
      for (const docSnap of auditLogsSnap.docs) {
        const deletedAuditLogRef = doc(
          db,
          "deleted",
          currentUser.id,
          "auditLogs",
          docSnap.id,
        );
        await setDoc(deletedAuditLogRef, {
          ...docSnap.data(),
          deletedAt: serverTimestamp(),
          deletedBy: auth.currentUser?.uid || currentUser.id,
        });
        try {
          await deleteDoc(docSnap.ref);
        } catch (err) {
          crashlytics.logError(
            `Error deleting auditLog: ${JSON.stringify({ path: docSnap.ref.path, error: err })}`,
            "AuthContext.tsx",
            err,
          );
        }
      }
      /* Cleaning up existing followers and activities */
      // Followers and following cleanup
      // Remove user from other users' lists using currentUser's own lists
      // Remove from followers' following
      if (Array.isArray(currentUser.followers)) {
        for (const followerId of currentUser.followers) {
          const followerRef = doc(db, "users", followerId);
          await updateDoc(followerRef, {
            following: arrayRemove(currentUser.id),
          });
        }
      }
      // Remove from following's followers
      if (Array.isArray(currentUser.following)) {
        for (const followingId of currentUser.following) {
          const followingRef = doc(db, "users", followingId);
          await updateDoc(followingRef, {
            followers: arrayRemove(currentUser.id),
          });
        }
      }
      // Remove from pendingFollowers' requestedFollowing
      if (Array.isArray(currentUser.pendingFollowers)) {
        for (const pendingFollowerId of currentUser.pendingFollowers) {
          const pendingFollowerRef = doc(db, "users", pendingFollowerId);
          await updateDoc(pendingFollowerRef, {
            requestedFollowing: arrayRemove(currentUser.id),
          });
        }
      }
      // Remove from requestedFollowing's pendingFollowers
      if (Array.isArray(currentUser.requestedFollowing)) {
        for (const requestedFollowingId of currentUser.requestedFollowing) {
          const requestedFollowingRef = doc(db, "users", requestedFollowingId);
          await updateDoc(requestedFollowingRef, {
            pendingFollowers: arrayRemove(currentUser.id),
          });
        }
      }
      // send push notification to followers about account deletion
      if (
        Array.isArray(currentUser.followers) &&
        currentUser.followers.length > 0
      ) {
        try {
          // create notification for each follower
          for (const followerId of currentUser.followers) {
            await createNotification(
              followerId,
              "ACCOUNT_DELETION",
              `${currentUser.username || "Someone"} has deleted their account and you have been removed from their followers`,
              `/profile/${currentUser.id}`,
            );
          }
        } catch (notificationError) {
          crashlytics.logError(
            `Error sending account deletion notifications: ${JSON.stringify(notificationError)}`,
            "AuthContext.tsx",
            notificationError,
          );
        }
      }
      // Activities related cleanup
      // Remove user from activities where they are a participant, kicked, or passed
      const participantQuery = query(
        collection(db, "activities"),
        where("participantIds", "array-contains", currentUser.id),
      );
      const kickedQuery = query(
        collection(db, "activities"),
        where("kickedParticipantIds", "array-contains", currentUser.id),
      );
      const passedQuery = query(
        collection(db, "activities"),
        where("passedParticipantIds", "array-contains", currentUser.id),
      );
      const [participantSnap, kickedSnap, passedSnap] = await Promise.all([
        getDocs(participantQuery),
        getDocs(kickedQuery),
        getDocs(passedQuery),
      ]);
      // Use a Set to avoid duplicate activity IDs
      const activityDocsMap = new Map();
      for (const snap of [participantSnap, kickedSnap, passedSnap]) {
        for (const docSnap of snap.docs) {
          activityDocsMap.set(docSnap.id, docSnap);
        }
      }
      for (const activityDocSnap of activityDocsMap.values()) {
        const activityData = activityDocSnap.data();
        let updateNeeded = false;
        const update: any = {};
        // Remove from participantIds
        let participantIds = Array.isArray(activityData.participantIds)
          ? activityData.participantIds.filter(
            (id: string) => id !== currentUser.id,
          )
          : [];
        if (
          participantIds.length !== (activityData.participantIds?.length || 0)
        ) {
          update.participantIds = participantIds;
          updateNeeded = true;
        }
        // Remove from kickedParticipantIds
        let kickedIds = Array.isArray(activityData.kickedParticipantIds)
          ? activityData.kickedParticipantIds.filter(
            (id: string) => id !== currentUser.id,
          )
          : [];
        if (
          kickedIds.length !== (activityData.kickedParticipantIds?.length || 0)
        ) {
          update.kickedParticipantIds = kickedIds;
          updateNeeded = true;
        }
        // Remove from passedParticipantIds
        let passedIds = Array.isArray(activityData.passedParticipantIds)
          ? activityData.passedParticipantIds.filter(
            (id: string) => id !== currentUser.id,
          )
          : [];
        if (
          passedIds.length !== (activityData.passedParticipantIds?.length || 0)
        ) {
          update.passedParticipantIds = passedIds;
          updateNeeded = true;
        }
        // If all participant arrays are now empty, move to deleted
        if (
          participantIds.length === 0 &&
          kickedIds.length === 0 &&
          passedIds.length === 0
        ) {
          const deletedActivityRef = doc(
            db,
            "deleted",
            currentUser.id,
            "activities",
            activityDocSnap.id,
          );
          await setDoc(deletedActivityRef, {
            ...activityData,
            deletedAt: serverTimestamp(),
            deletedBy: auth.currentUser?.uid || currentUser.id,
          });
          try {
            await deleteDoc(activityDocSnap.ref);
          } catch (err) {
            crashlytics.logError(
              `Error deleting activity: ${JSON.stringify({ path: activityDocSnap.ref.path, error: err })}`,
              "AuthContext.tsx",
              err,
            );
          }
          // Remove associated chats and notifications
          const chatsQuery = query(
            collection(db, "chats"),
            where("activityId", "==", activityDocSnap.id),
          );
          const chatsSnap = await getDocs(chatsQuery);
          for (const chatDocSnap of chatsSnap.docs) {
            try {
              await deleteDoc(chatDocSnap.ref);
            } catch (err) {
              crashlytics.logError(
                `Error deleting chat for activity: ${JSON.stringify({ path: chatDocSnap.ref.path, error: err })}`,
                "AuthContext.tsx",
                err,
              );
            }
          }
          const notificationsQuery = query(
            collection(db, "notifications"),
            where("activityId", "==", activityDocSnap.id),
          );
          const notificationsSnap = await getDocs(notificationsQuery);
          for (const notificationDocSnap of notificationsSnap.docs) {
            try {
              await deleteDoc(notificationDocSnap.ref);
            } catch (err) {
              crashlytics.logError(
                `Error deleting notification for activity: ${JSON.stringify({ path: notificationDocSnap.ref.path, error: err })}`,
                "AuthContext.tsx",
                err,
              );
            }
          }
          const auditLogsQuery = query(
            collection(db, "auditLogs"),
            where("activityId", "==", activityDocSnap.id),
          );
          const auditLogsSnap = await getDocs(auditLogsQuery);
          for (const auditLogDocSnap of auditLogsSnap.docs) {
            try {
              await deleteDoc(auditLogDocSnap.ref);
            } catch (err) {
              crashlytics.logError(
                `Error deleting auditLog for activity: ${JSON.stringify({ path: auditLogDocSnap.ref.path, error: err })}`,
                "AuthContext.tsx",
                err,
              );
            }
          }
        } else if (updateNeeded) {
          try {
            await updateDoc(activityDocSnap.ref, update);
          } catch (err) {
            crashlytics.logError(
              `Error updating activity: ${JSON.stringify({ path: activityDocSnap.ref.path, error: err })}`,
              "AuthContext.tsx",
              err,
            );
          }
        }
      }
      // Send push notification to other activity participants about activity updates
      for (const activityDocSnap of activityDocsMap.values()) {
        const activityData = activityDocSnap.data();
        // Collect all participant IDs (excluding the current user)
        const otherParticipantIds = [
          ...(Array.isArray(activityData.participantIds)
            ? activityData.participantIds
            : []),
        ].filter((id: string) => id !== currentUser.id);
        // Use a Set to avoid duplicate notifications
        const uniqueParticipantIds = Array.from(new Set(otherParticipantIds));
        for (const participantId of uniqueParticipantIds) {
          try {
            await createNotification(
              participantId,
              "ACCOUNT_DELETION",
              `${currentUser.username || "Someone"} has left or been removed from an activity you are part of`,
              `/activity/${activityDocSnap.id}`,
            );
          } catch (error) {
            crashlytics.logError?.(
              `Error sending activity update push notification: ${JSON.stringify(error)}`,
              "AuthContext.tsx",
              error,
            );
          }
        }
      }
      // Remove the user from chats where they are a participant
      const chatsQuery = query(
        collection(db, "chats"),
        where("participantIds", "array-contains", currentUser.id),
      );
      const chatsSnap = await getDocs(chatsQuery);
      for (const chatDocSnap of chatsSnap.docs) {
        const chatData = chatDocSnap.data();
        const updatedParticipantIds = Array.isArray(chatData.participantIds)
          ? chatData.participantIds.filter(
            (id: string) => id !== currentUser.id,
          )
          : [];
        if (updatedParticipantIds.length === 0) {
          // If no participants left, move to deleted
          const deletedChatRef = doc(
            db,
            "deleted",
            currentUser.id,
            "chats",
            chatDocSnap.id,
          );
          await setDoc(deletedChatRef, {
            ...chatData,
            deletedAt: serverTimestamp(),
            deletedBy: auth.currentUser?.uid || currentUser.id,
          });
          try {
            await deleteDoc(chatDocSnap.ref);
          } catch (err) {
            crashlytics.logError(
              `Error deleting chat: ${JSON.stringify({ path: chatDocSnap.ref.path, error: err })}`,
              "AuthContext.tsx",
              err,
            );
          }
        } else {
          try {
            await updateDoc(chatDocSnap.ref, {
              participantIds: updatedParticipantIds,
            });
          } catch (err) {
            crashlytics.logError(
              `Error updating chat: ${JSON.stringify({ path: chatDocSnap.ref.path, error: err })}`,
              "AuthContext.tsx",
              err,
            );
          }
        }
      }

      /* Now move the activities created by the user to deleted collection */
      // Activities
      const activitiesSnap = await getDocs(
        query(
          collection(db, "activities"),
          where("creatorId", "==", currentUser.id),
        ),
      );
      for (const docSnap of activitiesSnap.docs) {
        const deletedActivityRef = doc(
          db,
          "deleted",
          currentUser.id,
          "activities",
          docSnap.id,
        );
        await setDoc(deletedActivityRef, {
          ...docSnap.data(),
          deletedAt: serverTimestamp(),
          deletedBy: auth.currentUser?.uid || currentUser.id,
        });
        try {
          await deleteDoc(docSnap.ref);
        } catch (err) {
          crashlytics.logError(
            `Error deleting created activity: ${JSON.stringify({ path: docSnap.ref.path, error: err })}`,
            "AuthContext.tsx",
            err,
          );
        }
        // Remove associated chats
        const chatsQuery = query(
          collection(db, "chats"),
          where("activityId", "==", docSnap.id),
        );
        const chatsSnap = await getDocs(chatsQuery);
        for (const chatDocSnap of chatsSnap.docs) {
          try {
            await deleteDoc(chatDocSnap.ref);
          } catch (err) {
            crashlytics.logError(
              `Error deleting chat for created activity: ${JSON.stringify({ path: chatDocSnap.ref.path, error: err })}`,
              "AuthContext.tsx",
              err,
            );
          }
        }
        // Remove associated notifications
        const notificationsQuery = query(
          collection(db, "notifications"),
          where("activityId", "==", docSnap.id),
        );
        const notificationsSnap = await getDocs(notificationsQuery);
        for (const notificationDocSnap of notificationsSnap.docs) {
          try {
            await deleteDoc(notificationDocSnap.ref);
          } catch (err) {
            crashlytics.logError(
              `Error deleting notification for created activity: ${JSON.stringify({ path: notificationDocSnap.ref.path, error: err })}`,
              "AuthContext.tsx",
              err,
            );
          }
        }
        // Remove associated audit logs
        const auditLogsQuery = query(
          collection(db, "auditLogs"),
          where("activityId", "==", docSnap.id),
        );
        const auditLogsSnap = await getDocs(auditLogsQuery);
        for (const auditLogDocSnap of auditLogsSnap.docs) {
          try {
            await deleteDoc(auditLogDocSnap.ref);
          } catch (err) {
            crashlytics.logError(
              `Error deleting auditLog for created activity: ${JSON.stringify({ path: auditLogDocSnap.ref.path, error: err })}`,
              "AuthContext.tsx",
              err,
            );
          }
        }
      }
      // Move chats associated with deleted user to deleted collection
      const userChatsQuery = query(
        collection(db, "chats"),
        where("participantIds", "array-contains", currentUser.id),
      );
      const userChatsSnap = await getDocs(userChatsQuery);
      for (const docSnap of userChatsSnap.docs) {
        const deletedChatRef = doc(
          db,
          "deleted",
          currentUser.id,
          "chats",
          docSnap.id,
        );
        await setDoc(deletedChatRef, {
          ...docSnap.data(),
          deletedAt: serverTimestamp(),
          deletedBy: auth.currentUser?.uid || currentUser.id,
        });
        try {
          await deleteDoc(docSnap.ref);
        } catch (err) {
          crashlytics.logError(
            `Error deleting user chat: ${JSON.stringify({ path: docSnap.ref.path, error: err })}`,
            "AuthContext.tsx",
            err,
          );
        }
      }
      // Move all chats for the activities that were deleted or moved above
      const activityIds = activitiesSnap.docs.map((docSnap) => docSnap.id);
      if (activityIds.length > 0) {
        const relatedChatsQuery = query(
          collection(db, "chats"),
          where("activityId", "in", activityIds),
        );
        const relatedChatsSnap = await getDocs(relatedChatsQuery);
        for (const docSnap of relatedChatsSnap.docs) {
          const deletedChatRef = doc(
            db,
            "deleted",
            currentUser.id,
            "chats",
            docSnap.id,
          );
          await setDoc(deletedChatRef, {
            ...docSnap.data(),
            deletedAt: serverTimestamp(),
            deletedBy: auth.currentUser?.uid || currentUser.id,
          });
          try {
            await deleteDoc(docSnap.ref);
          } catch (err) {
            crashlytics.logError(
              `Error deleting related chat: ${JSON.stringify({ path: docSnap.ref.path, error: err })}`,
              "AuthContext.tsx",
              err,
            );
          }
        }
      }
      // Move all audit logs for the activities that were deleted or moved above
      if (activityIds.length > 0) {
        const relatedAuditLogsQuery = query(
          collection(db, "auditLogs"),
          where("activityId", "in", activityIds),
        );
        const relatedAuditLogsSnap = await getDocs(relatedAuditLogsQuery);
        for (const docSnap of relatedAuditLogsSnap.docs) {
          const deletedAuditLogRef = doc(
            db,
            "deleted",
            currentUser.id,
            "auditLogs",
            docSnap.id,
          );
          await setDoc(deletedAuditLogRef, {
            ...docSnap.data(),
            deletedAt: serverTimestamp(),
            deletedBy: auth.currentUser?.uid || currentUser.id,
          });
          try {
            await deleteDoc(docSnap.ref);
          } catch (err) {
            crashlytics.logError(
              `Error deleting related auditLog: ${JSON.stringify({ path: docSnap.ref.path, error: err })}`,
              "AuthContext.tsx",
              err,
            );
          }
        }
      }
      // Notify the participants of deleted activities
      for (const docSnap of activitiesSnap.docs) {
        const activityData = docSnap.data();
        if (Array.isArray(activityData.participantIds)) {
          for (const participantId of activityData.participantIds) {
            if (participantId !== currentUser.id) {
              try {
                await createNotification(
                  participantId,
                  "ACCOUNT_DELETION",
                  `${currentUser.username || "Someone"} has deleted her account and you have been removed from an activity you were part of`,
                  `/activity/${docSnap.id}`,
                );
              } catch (pushError) {
                crashlytics.logError?.(
                  `Error sending deleted activity push notification: ${JSON.stringify(pushError)}`,
                  "AuthContext.tsx",
                  pushError,
                );
              }
            }
          }
        }
      }

      /* Finally, move user document to deleted collection within a transaction */

      const userRef = doc(db, "users", currentUser.id);
      const deletedUserRef = doc(
        db,
        "deleted",
        currentUser.id,
        "users",
        currentUser.id,
      );
      await runTransaction(db, async (transaction) => {
        const userSnap = await transaction.get(userRef);
        if (!userSnap.exists()) {
          crashlytics.log(
            `User already deleted: ${currentUser.email} (${currentUser.id})`,
          );
          return;
        }
        const userData = userSnap.data();
        // Move user to deleted collection
        transaction.set(deletedUserRef, {
          ...userData,
          deletedAt: serverTimestamp(),
          deletedBy: auth.currentUser?.uid || currentUser.id,
        });
        transaction.delete(userRef);
      });
      // Remove notifications again related to the user as they were sent for deletion of activities above
      const notificationsSnap2 = await getDocs(
        query(
          collection(db, "notifications"),
          where("userId", "==", currentUser.id),
        ),
      );
      for (const docSnap of notificationsSnap2.docs) {
        const deletedNotificationRef = doc(
          db,
          "deleted",
          currentUser.id,
          "notifications",
          docSnap.id,
        );
        await setDoc(deletedNotificationRef, {
          ...docSnap.data(),
          deletedAt: serverTimestamp(),
          deletedBy: auth.currentUser?.uid || currentUser.id,
        });
        try {
          await deleteDoc(docSnap.ref);
        } catch (err) {
          crashlytics.logError(
            `Error deleting notification (final cleanup): ${JSON.stringify({ path: docSnap.ref.path, error: err })}`,
            "AuthContext.tsx",
            err,
          );
        }
      }
      crashlytics.log(
        `User account and related data moved to deleted: ${currentUser.email} (${currentUser.id})`,
      );
    } catch (error) {
      if (
        (error as any)?.code === "failed-precondition" ||
        (error as any)?.code === "not-found" ||
        (typeof error === "string" && error.includes("not found"))
      ) {
        crashlytics.log(
          `User already deleted or precondition failed: ${currentUser.email} (${currentUser.id})`,
        );
        return;
      }
      crashlytics.logError(
        `Error deleting user account: ${currentUser.email} (${currentUser.id})(${JSON.stringify(error)})`,
        "AuthContext.tsx",
        error,
      );
      throw error;
    }
  }, [currentUser, logout]);

  // remove auth user
  const deleteAuthUser = useCallback(async () => {
    if (auth.currentUser) {
      const email = auth.currentUser.email;
      const uid = auth.currentUser.uid;
      try {
        await auth.currentUser.delete();
        crashlytics.log(`User Auth account deleted: ${email} (${uid})`);
      } catch (error: any) {
        crashlytics.logError(
          `Error deleting Firebase Auth user: ${JSON.stringify(error?.message)}`,
          "AuthContext.tsx",
          error,
        );
        throw error;
      }
    }
  }, [currentUser, logout]);

  const resendVerificationEmail = async () => {
    if (!auth.currentUser) {
      throw new Error("No user is currently signed in");
    }

    if (auth.currentUser.emailVerified) {
      throw new Error("Email is already verified");
    }

    try {
      await sendEmailVerification(auth.currentUser);
    } catch (error) {
      crashlytics.logError(
        `Error sending verification email: ${JSON.stringify(error)}`,
        "AuthContext.tsx",
        error,
      );
      throw new Error(
        "Give it time. Please do check your spam folder as well.",
      );
    }
  };

  const isEmailVerified = (): boolean => {
    if (!auth.currentUser) return false;

    // Force reload the user to get the latest emailVerified status
    auth.currentUser.reload().catch(() => {
      // Ignore errors, use cached value
    });

    return auth.currentUser.emailVerified || false;
  };

  const getAllUsers = useCallback(async (): Promise<User[]> => {
    if (!db) return [];
    const usersCollection = collection(db, "users");
    const userSnapshot = await getDocs(usersCollection);
    return userSnapshot.docs.map(
      (doc) => ({ ...doc.data(), id: doc.id }) as User,
    );
  }, []);

  const searchUsers = useCallback(
    async (
      searchQuery: string,
      lastDoc?: any,
      pageLimit: number = 20,
    ): Promise<{ users: User[]; lastDoc: any; hasMore: boolean }> => {
      if (!db) return { users: [], lastDoc: null, hasMore: false };
      const usersCollection = collection(db, "users");
      const searchQueryLower = searchQuery.toLowerCase().trim();

      if (searchQueryLower === "") {
        // If no search query, return all users paginated
        let q = query(usersCollection, orderBy("username"), limit(pageLimit));

        if (lastDoc) {
          q = query(
            usersCollection,
            orderBy("username"),
            startAfter(lastDoc),
            limit(pageLimit),
          );
        }

        const querySnapshot = await getDocs(q);
        const users = querySnapshot.docs.map(
          (doc) => ({ ...doc.data(), id: doc.id }) as User,
        );
        const newLastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
        const hasMore = querySnapshot.docs.length === pageLimit;

        return { users, lastDoc: newLastDoc, hasMore };
      } else {
        // For search queries, we need to get all users and filter client-side
        // This is because Firestore doesn't support text search natively
        // In a production app, you'd want to use Algolia or similar for better search
        const userSnapshot = await getDocs(usersCollection);
        const allUsers = userSnapshot.docs.map(
          (doc) => ({ ...doc.data(), id: doc.id }) as User,
        );

        const filteredUsers = allUsers.filter(
          (user) =>
            user?.username?.toLowerCase().includes(searchQueryLower) ||
            user?.location?.toLowerCase().includes(searchQueryLower) ||
            user?.bio?.toLowerCase().includes(searchQueryLower) ||
            user?.interests?.some((interest) =>
              interest?.toLowerCase().includes(searchQueryLower),
            ),
        );

        // Implement client-side pagination for search results
        const startIndex = lastDoc ? lastDoc.startIndex + pageLimit : 0;
        const endIndex = startIndex + pageLimit;
        const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
        const newLastDoc =
          endIndex < filteredUsers.length ? { startIndex } : null;
        const hasMore = endIndex < filteredUsers.length;

        return { users: paginatedUsers, lastDoc: newLastDoc, hasMore };
      }
    },
    [],
  );

  const getUserById = useCallback(
    async (id: string): Promise<User | undefined> => {
      if (!db) return undefined;
      const userDoc = doc(db, "users", id);
      const docSnap = await getDoc(userDoc);
      return docSnap.exists()
        ? ({ ...docSnap.data(), id: docSnap.id } as User)
        : undefined;
    },
    [],
  );

  const getUsersByIds = useCallback(async (ids: string[]): Promise<User[]> => {
    if (ids.length === 0) return [];
    // Firestore 'in' queries are limited to 30 items.
    // We chunk the requests to handle more than 30 IDs.
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += 30) {
      chunks.push(ids.slice(i, i + 30));
    }

    const userPromises = chunks.map(async (chunk) => {
      if (!db) return [];
      const usersCollection = collection(db, "users");
      const q = query(usersCollection, where("__name__", "in", chunk));
      const querySnapshot = await getDocs(q);
      // Only return users whose doc.id is in the chunk (should always be true, but extra safety)
      return querySnapshot.docs
        .filter((doc) => chunk.includes(doc.id))
        .map((doc) => ({ ...doc.data(), id: doc.id }) as User);
    });

    let userArrays: User[][] = [];
    try {
      userArrays = await Promise.all(userPromises);
    } catch (err) {
      // If any chunk fails, log the error and continue with successful ones
      crashlytics?.logError?.(
        `Error in getUsersByIds Promise.all: ${JSON.stringify(err)}`,
        "AuthContext.tsx",
        err,
      );
      // Try to get results from settled promises
      const results = await Promise.allSettled(userPromises);
      userArrays = results
        .filter((r) => r.status === "fulfilled")
        .map((r) => (r as PromiseFulfilledResult<User[]>).value);
    }
    // filter empty users
    return userArrays
      .flat()
      .filter((user) => user !== undefined && user !== null);
  }, []);

  const createAuditLog = useCallback(
    async (type: string, text: string, link: string, fromUserId: string) => {
      if (!db || !currentUser) return;
      try {
        await addDoc(collection(db, "auditLogs"), {
          type,
          text,
          link,
          fromUserId,
          userId: currentUser.id,
          createdAt: serverTimestamp(),
          activityId:
            link && link.startsWith("/activity/")
              ? link.replace("/activity/", "")
              : null,
        });
      } catch (err: any) {
        // If error is already-exists, ignore, else rethrow
        if (err?.code !== "already-exists") {
          throw err;
        }
      }
    },
    [currentUser],
  );

  const updateUser = async (updatedUserData: Partial<User>) => {
    if (!db || !currentUser) return;

    // Check if username is being updated and if it's different from current username
    if (
      updatedUserData.username &&
      updatedUserData.username !== currentUser.username
    ) {
      const usernameExists = await checkUsernameExists(
        updatedUserData.username,
      );
      if (usernameExists) {
        throw new Error(
          "This username is already taken. Please try a different one.",
        );
      }
    }

    const userDocRef = doc(db, "users", currentUser.id);

    // Check if profile picture is being updated
    if (
      updatedUserData.profileImageUrl &&
      updatedUserData.profileImageUrl !== currentUser.profileImageUrl
    ) {
      await createAuditLog(
        "PROFILE_UPDATE",
        `${currentUser.username} updated your <strong>profile picture</strong>`,
        "/profile",
        currentUser.id,
      );
    }

    await updateDoc(userDocRef, {
      ...updatedUserData,
      updatedAt: new Date().toISOString(),
    });

    // Log analytics event
    analytics.logEvent("profile_updated", {
      userId: currentUser.id,
      timestamp: new Date().toISOString(),
      updatedFields: Object.keys(updatedUserData),
    });
    crashlytics.log(
      `User Profile Updated: ${currentUser.id}, Fields: ${Object.keys(updatedUserData).join(", ")}`,
    );
  };

  const createNotification = useCallback(
    async (
      targetUserId: string,
      type:
        | "FOLLOW_REQUEST"
        | "FOLLOW_ACCEPT"
        | "FOLLOW_DECLINE"
        | "MUTUAL_MATCH"
        | "FOLLOWER_REMOVED"
        | "ACCOUNT_DELETION",
      text: string,
      link: string,
    ) => {
      if (!db || !currentUser) return;
      try {
        await addDoc(collection(db, "notifications"), {
          userId: targetUserId,
          type,
          text,
          link,
          chatId: "", // Empty for follow notifications
          isRead: false,
          fromUserId: currentUser.id,
          createdAt: serverTimestamp(),
        });
      } catch (err: any) {
        // If error is already-exists, ignore, else rethrow
        if (err?.code !== "already-exists") {
          throw err;
        }
      }
    },
    [currentUser],
  );

  const runAuthBackgroundTask = useCallback(
    (task: Promise<unknown>, description: string) => {
      void task.catch((error) => {
        crashlytics.logError(
          `${description}: ${JSON.stringify((error as any)?.message || error)}`,
          "AuthContext.tsx",
          error,
        );
      });
    },
    [],
  );

  const requestFollow = async (targetUserId: string) => {
    if (!db || !currentUser || currentUser.id === targetUserId) return;

    if ((currentUser.requestedFollowing ?? []).includes(targetUserId)) {
      return;
    }

    const previousUser = currentUser;
    const batch = writeBatch(db);

    const currentUserRef = doc(db, "users", currentUser.id);
    batch.update(currentUserRef, {
      requestedFollowing: arrayUnion(targetUserId),
    });

    const targetUserRef = doc(db, "users", targetUserId);
    batch.update(targetUserRef, {
      pendingFollowers: arrayUnion(currentUser.id),
    });

    setCurrentUser((prev) =>
      prev ? applyOptimisticRequestFollow(prev, targetUserId) : prev,
    );

    try {
      await batch.commit();
    } catch (error) {
      setCurrentUser(previousUser);
      throw error;
    }

    runAuthBackgroundTask(
      (async () => {
        const targetUser = await getUserById(targetUserId);
        const mutualFollowers = targetUser
          ? (previousUser.followers ?? []).filter((f) =>
            (targetUser.followers ?? []).includes(f),
          ).length
          : 0;

        await queueUserAction(
          previousUser.id,
          "follow_request",
          undefined,
          `Following ${targetUser?.username || "user"}`,
          undefined,
          targetUser?.location,
          {
            targetUserId,
            targetUsername: targetUser?.username,
            targetAge: CommonFunctions.calculateAge(targetUser?.dateOfBirth),
            targetLocation: targetUser?.location,
            mutualFollowers,
            source: "social_action",
          },
        );

        await createNotification(
          targetUserId,
          "FOLLOW_REQUEST",
          `${previousUser.username || "Someone"} wants to follow you`,
          `/profile/${previousUser.id}`,
        );
      })(),
      `Error completing follow request for ${targetUserId}`,
    );
  };

  const cancelFollowRequest = async (targetUserId: string) => {
    if (!db || !currentUser) return;

    if (!(currentUser.requestedFollowing ?? []).includes(targetUserId)) {
      return;
    }

    const previousUser = currentUser;
    const batch = writeBatch(db);

    const currentUserRef = doc(db, "users", currentUser.id);
    batch.update(currentUserRef, {
      requestedFollowing: arrayRemove(targetUserId),
    });

    const targetUserRef = doc(db, "users", targetUserId);
    batch.update(targetUserRef, {
      pendingFollowers: arrayRemove(currentUser.id),
    });

    setCurrentUser((prev) =>
      prev ? applyOptimisticCancelFollowRequest(prev, targetUserId) : prev,
    );

    try {
      await batch.commit();
    } catch (error) {
      setCurrentUser(previousUser);
      throw error;
    }

    runAuthBackgroundTask(
      (async () => {
        const targetUser = await getUserById(targetUserId);

        await queueUserAction(
          previousUser.id,
          "cancel_follow_request",
          undefined,
          `Cancelled request to ${targetUser?.username || "user"}`,
          undefined,
          targetUser?.location,
          {
            targetUserId,
            targetUsername: targetUser?.username,
            targetLocation: targetUser?.location,
            source: "social_action",
          },
        );

        const followRequestNotifications = await getDocs(
          query(
            collection(db, "notifications"),
            where("userId", "==", targetUserId),
            where("fromUserId", "==", previousUser.id),
            where("type", "==", "FOLLOW_REQUEST"),
            where("isRead", "==", false),
          ),
        );

        if (followRequestNotifications.docs.length > 0) {
          const notificationBatch = writeBatch(db);
          followRequestNotifications.docs.forEach((docSnap) => {
            notificationBatch.delete(docSnap.ref);
          });
          await notificationBatch.commit();
        }
      })(),
      `Error cancelling follow request for ${targetUserId}`,
    );
  };

  const acceptFollowRequest = async (requesterId: string) => {
    if (!db || !currentUser) return;

    const previousUser = currentUser;

    try {
      const batch = writeBatch(db);

      const currentUserRef = doc(db, "users", currentUser.id);
      batch.update(currentUserRef, {
        followers: arrayUnion(requesterId),
        pendingFollowers: arrayRemove(requesterId),
      });

      const requesterUserRef = doc(db, "users", requesterId);
      batch.update(requesterUserRef, {
        following: arrayUnion(currentUser.id),
        requestedFollowing: arrayRemove(currentUser.id),
      });

      setCurrentUser((prev) =>
        prev ? applyOptimisticAcceptFollowRequest(prev, requesterId) : prev,
      );

      await batch.commit();
    } catch (error) {
      setCurrentUser(previousUser);
      throw error;
    }

    runAuthBackgroundTask(
      (async () => {
        const requesterUser = await getUserById(requesterId);
        const mutualFollowers = requesterUser
          ? (previousUser.followers ?? []).filter((f) =>
            (requesterUser.followers ?? []).includes(f),
          ).length
          : 0;

        await queueUserAction(
          previousUser.id,
          "follow_accept",
          undefined,
          `Accepted ${requesterUser?.username || "user"}`,
          undefined,
          requesterUser?.location,
          {
            targetUserId: requesterId,
            targetUsername: requesterUser?.username,
            targetAge: CommonFunctions.calculateAge(requesterUser?.dateOfBirth),
            targetLocation: requesterUser?.location,
            mutualFollowers,
            source: "social_action",
          },
        );

        const followRequestNotifications = await getDocs(
          query(
            collection(db, "notifications"),
            where("userId", "==", previousUser.id),
            where("fromUserId", "==", requesterId),
            where("type", "==", "FOLLOW_REQUEST"),
            where("isRead", "==", false),
          ),
        );

        if (followRequestNotifications.docs.length > 0) {
          const notificationBatch = writeBatch(db);
          followRequestNotifications.docs.forEach((docSnap) => {
            notificationBatch.update(docSnap.ref, { isRead: true });
          });
          await notificationBatch.commit();
        }

        await createNotification(
          requesterId,
          "FOLLOW_ACCEPT",
          `${previousUser.username || "Someone"} accepted your follow request`,
          `/profile/${previousUser.id}`,
        );
      })(),
      `Error accepting follow request from ${requesterId}`,
    );
  };

  const declineFollowRequest = async (requesterId: string) => {
    if (!db || !currentUser) return;

    const previousUser = currentUser;
    const batch = writeBatch(db);

    const currentUserRef = doc(db, "users", currentUser.id);
    batch.update(currentUserRef, {
      pendingFollowers: arrayRemove(requesterId),
    });

    const requesterUserRef = doc(db, "users", requesterId);
    batch.update(requesterUserRef, {
      requestedFollowing: arrayRemove(currentUser.id),
    });

    setCurrentUser((prev) =>
      prev ? applyOptimisticDeclineFollowRequest(prev, requesterId) : prev,
    );

    try {
      await batch.commit();
    } catch (error) {
      setCurrentUser(previousUser);
      throw error;
    }

    runAuthBackgroundTask(
      (async () => {
        const requesterUser = await getUserById(requesterId);

        await queueUserAction(
          previousUser.id,
          "follow_decline",
          undefined,
          `Declined ${requesterUser?.username || "user"}`,
          undefined,
          requesterUser?.location,
          {
            targetUserId: requesterId,
            targetUsername: requesterUser?.username,
            targetAge: CommonFunctions.calculateAge(requesterUser?.dateOfBirth),
            targetLocation: requesterUser?.location,
            source: "social_action",
          },
        );

        const followRequestNotifications = await getDocs(
          query(
            collection(db, "notifications"),
            where("userId", "==", previousUser.id),
            where("fromUserId", "==", requesterId),
            where("type", "==", "FOLLOW_REQUEST"),
            where("isRead", "==", false),
          ),
        );

        if (followRequestNotifications.docs.length > 0) {
          const notificationBatch = writeBatch(db);
          followRequestNotifications.docs.forEach((docSnap) => {
            notificationBatch.update(docSnap.ref, { isRead: true });
          });
          await notificationBatch.commit();
        }

        await createNotification(
          requesterId,
          "FOLLOW_DECLINE",
          `${previousUser.username || "Someone"} declined your follow request`,
          `/profile/${previousUser.id}`,
        );
      })(),
      `Error declining follow request from ${requesterId}`,
    );
  };

  const unfollowUser = async (targetUserId: string) => {
    if (!db || !currentUser) return;

    if (!(currentUser.following ?? []).includes(targetUserId)) {
      return;
    }

    const previousUser = currentUser;
    const batch = writeBatch(db);

    const currentUserRef = doc(db, "users", currentUser.id);
    batch.update(currentUserRef, { following: arrayRemove(targetUserId) });

    const targetUserRef = doc(db, "users", targetUserId);
    batch.update(targetUserRef, { followers: arrayRemove(currentUser.id) });

    setCurrentUser((prev) =>
      prev ? applyOptimisticUnfollow(prev, targetUserId) : prev,
    );

    try {
      await batch.commit();
    } catch (error) {
      setCurrentUser(previousUser);
      throw error;
    }

    runAuthBackgroundTask(
      (async () => {
        const targetUser = await getUserById(targetUserId);

        await queueUserAction(
          previousUser.id,
          "unfollow",
          undefined,
          `Unfollowed ${targetUser?.username || "user"}`,
          undefined,
          targetUser?.location,
          {
            targetUserId,
            targetUsername: targetUser?.username,
            targetAge: CommonFunctions.calculateAge(targetUser?.dateOfBirth),
            targetLocation: targetUser?.location,
            source: "social_action",
          },
        );
      })(),
      `Error unfollowing user ${targetUserId}`,
    );
  };

  const establishMutualFollow = async (targetUserId: string) => {
    if (!db || !currentUser) return;

    if ((currentUser.following ?? []).includes(targetUserId)) {
      return;
    }

    const previousUser = currentUser;
    const batch = writeBatch(db);

    const currentUserRef = doc(db, "users", currentUser.id);
    batch.update(currentUserRef, { following: arrayUnion(targetUserId) });

    const targetUserRef = doc(db, "users", targetUserId);
    batch.update(targetUserRef, { followers: arrayUnion(currentUser.id) });

    setCurrentUser((prev) =>
      prev ? applyOptimisticMutualFollow(prev, targetUserId) : prev,
    );

    try {
      await batch.commit();
    } catch (error) {
      setCurrentUser(previousUser);
      throw error;
    }

    runAuthBackgroundTask(
      (async () => {
        const targetUser = await getUserById(targetUserId);
        const mutualFollowers = targetUser
          ? (previousUser.followers ?? []).filter((f: string) =>
            (targetUser.followers ?? []).includes(f),
          ).length
          : 0;

        await queueUserAction(
          previousUser.id,
          "mutual_follow",
          undefined,
          `Mutual match with ${targetUser?.username || "user"}`,
          undefined,
          targetUser?.location,
          {
            targetUserId,
            targetUsername: targetUser?.username,
            targetAge: CommonFunctions.calculateAge(targetUser?.dateOfBirth),
            targetLocation: targetUser?.location,
            mutualFollowers,
            source: "social_action",
            mutualMatchAction: true,
          },
        );

        if (targetUser) {
          await createNotification(
            targetUserId,
            "MUTUAL_MATCH",
            `${previousUser.username} and ${targetUser.username} are now following each other!`,
            `/profile/${previousUser.id}`,
          );
          await createNotification(
            previousUser.id,
            "MUTUAL_MATCH",
            `${previousUser.username} and ${targetUser.username} are now following each other!`,
            `/profile/${targetUserId}`,
          );
        }
      })(),
      `Error creating mutual follow with ${targetUserId}`,
    );
  };

  const removeFollower = async (followerId: string) => {
    if (!db || !currentUser) return;

    if (!(currentUser.followers ?? []).includes(followerId)) {
      return;
    }

    const previousUser = currentUser;
    const batch = writeBatch(db);

    // Remove follower from current user's followers list
    const currentUserRef = doc(db, "users", currentUser.id);
    batch.update(currentUserRef, { followers: arrayRemove(followerId) });

    // Remove current user from follower's following list
    const followerUserRef = doc(db, "users", followerId);
    batch.update(followerUserRef, { following: arrayRemove(currentUser.id) });

    setCurrentUser((prev) =>
      prev ? applyOptimisticRemoveFollower(prev, followerId) : prev,
    );

    try {
      await batch.commit();
    } catch (error) {
      setCurrentUser(previousUser);
      throw error;
    }

    runAuthBackgroundTask(
      (async () => {
        const followerUser = await getUserById(followerId);

        await queueUserAction(
          previousUser.id,
          "remove_follower",
          undefined,
          `Removed ${followerUser?.username || "user"}`,
          undefined,
          followerUser?.location,
          {
            targetUserId: followerId,
            targetUsername: followerUser?.username,
            targetAge: CommonFunctions.calculateAge(followerUser?.dateOfBirth),
            targetLocation: followerUser?.location,
            source: "social_action",
          },
        );

        await createNotification(
          followerId,
          "FOLLOWER_REMOVED",
          `${previousUser.username || "Someone"} removed you from their followers`,
          `/profile/${previousUser.id}`,
        );
      })(),
      `Error removing follower ${followerId}`,
    );
  };

  const markOnboardingComplete = async () => {
    if (!currentUser) return;

    if (currentUser.hasCompletedOnboarding && currentUser.signupMetadata) {
      return;
    }

    // Only fetch metadata if we don't already have it stored
    const signupMethod =
      (currentUser.signupMethod as SignupMetadata["signupMethod"]) ||
      currentUser.signupMetadata?.signupMethod ||
      "email";

    let signupMetadata: SignupMetadata | undefined;
    if (!currentUser.signupMetadata) {
      try {
        signupMetadata = await getSignupMetadata(
          signupMethod,
          "onboarding_complete",
        );
      } catch (err: any) {
        crashlytics.logError(
          `Failed to collect signup metadata on onboarding complete: ${err?.message || "unknown"}`,
          "AuthContext.tsx",
          err,
        );
      }
    }

    const updates: Partial<User> = {
      hasCompletedOnboarding: true,
      signupMethod,
    };

    if (signupMetadata) {
      updates.signupMetadata = signupMetadata;
      updates.lastKnownCountry = signupMetadata.locationInfo?.country;
      updates.lastKnownCity = signupMetadata.locationInfo?.city;
      updates.lastKnownTimezone = signupMetadata.deviceInfo.timezone;
    }

    await updateUser(CommonFunctions.removeUndefined(updates));
  };

  const updateSignupMetadataPermissions = useCallback(async () => {
    if (!currentUser || !currentUser.hasCompletedOnboarding) return;

    try {
      const permissions = await getPermissionSnapshot();
      const existingPermissions = currentUser.signupMetadata?.permissions;

      if (
        existingPermissions?.location === permissions.location &&
        existingPermissions?.notifications === permissions.notifications
      ) {
        return;
      }

      const signupMethod =
        (currentUser.signupMethod as SignupMetadata["signupMethod"]) ||
        currentUser.signupMetadata?.signupMethod ||
        "email";

      const baseMetadata =
        currentUser.signupMetadata ||
        (await getSignupMetadata(signupMethod, "permissions_change"));

      if (!baseMetadata) return;

      await updateUser({
        signupMetadata: {
          ...baseMetadata,
          permissions,
        },
      });
    } catch (err: any) {
      crashlytics.logError(
        `Failed to update signup metadata permissions: ${err?.message || "unknown"}`,
        "AuthContext.tsx",
        err,
      );
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser || !currentUser.hasCompletedOnboarding) return;

    let isActive = true;
    const cleanupListeners: Array<() => void> = [];

    const syncPermissions = async () => {
      if (!isActive) return;
      await updateSignupMetadataPermissions();
    };

    syncPermissions();

    if (
      typeof navigator !== "undefined" &&
      "permissions" in navigator &&
      (navigator as any).permissions?.query
    ) {
      const permissionNames: PermissionName[] = [
        "geolocation",
        "notifications",
      ];

      permissionNames.forEach((name) => {
        (navigator as any).permissions
          .query({ name } as PermissionDescriptor)
          .then((status: PermissionStatus) => {
            if (!status) return;

            const handler = () => {
              syncPermissions();
            };

            if (typeof status.addEventListener === "function") {
              status.addEventListener("change", handler);
              cleanupListeners.push(() =>
                status.removeEventListener("change", handler),
              );
            } else {
              status.onchange = handler;
              cleanupListeners.push(() => {
                if (status.onchange === handler) {
                  status.onchange = null;
                }
              });
            }
          })
          .catch((err: any) => {
            crashlytics.logError(
              `Failed to watch ${String(name)} permission: ${err?.message || "unknown"}`,
              "AuthContext.tsx",
              err,
            );
          });
      });
    }

    return () => {
      isActive = false;
      cleanupListeners.forEach((cleanup) => cleanup());
    };
  }, [currentUser, updateSignupMetadataPermissions]);

  const linkGoogleAccount = async () => {
    try {
      if (!auth.currentUser) {
        throw new Error(
          "No user is currently signed in. Please sign in first.",
        );
      }

      // Check if Google is already linked
      const isGoogleLinked = auth.currentUser.providerData.some(
        (provider: { providerId: string }) =>
          provider.providerId === "google.com",
      );

      if (isGoogleLinked) {
        throw new Error("Google account is already linked to this account.");
      }

      if (Capacitor.isNativePlatform()) {
        // Native: Use plugin

        // Force logout from Google to ensure account picker appears
        try {
          await SocialLogin.logout({ provider: "google" });
        } catch (e) {
          // Ignore error
        }

        const res = await SocialLogin.login({
          provider: "google",
          options: {
            scopes: ["email", "profile"],
            forceRefreshToken: true,
          },
        });

        if (
          !res ||
          !res.result ||
          res.result.responseType !== "online" ||
          !res.result.idToken
        ) {
          throw new Error("Google sign-in failed or was cancelled.");
        }

        const credential = GoogleAuthProvider.credential(res.result.idToken);
        await linkWithCredential(auth.currentUser, credential);
      } else {
        // Web: Use popup
        const provider = new GoogleAuthProvider();
        provider.addScope("email");
        provider.addScope("profile");

        await linkWithPopup(
          auth.currentUser,
          provider,
          browserPopupRedirectResolver,
        );
      }

      // Log analytics event
      analytics.logEvent("account_linked", {
        provider: "google",
        timestamp: new Date().toISOString(),
      });
      crashlytics.log(`User Linked Google Account: ${auth.currentUser.uid}`);
    } catch (error: any) {
      crashlytics.logError(
        `Error linking Google account: ${JSON.stringify(error?.message)}`,
        "AuthContext.tsx",
        error,
      );

      if (error.code === "auth/credential-already-in-use") {
        throw new Error(
          "This Google account is already linked to another user.",
        );
      } else if (error.code === "auth/provider-already-linked") {
        throw new Error("You already have a Google account linked.");
      } else if (error.code === "auth/popup-closed-by-user") {
        throw new Error("Link cancelled.");
      }

      throw error;
    }
  };

  const linkAppleAccount = async () => {
    try {
      if (!auth.currentUser) {
        throw new Error(
          "No user is currently signed in. Please sign in first.",
        );
      }

      // Check if Apple is already linked
      const isAppleLinked = auth.currentUser.providerData.some(
        (provider: { providerId: string }) =>
          provider.providerId === "apple.com",
      );

      if (isAppleLinked) {
        throw new Error("Apple account is already linked to this account.");
      }

      if (Capacitor.isNativePlatform()) {
        // Native: Use plugin
        const res = await SocialLogin.login({
          provider: "apple",
          options: {
            scopes: ["email", "name"],
          },
        });

        if (!res || !res.result || !(res.result as any).identityToken) {
          throw new Error("Apple sign-in failed or was cancelled.");
        }

        const provider = new OAuthProvider("apple.com");
        const credential = provider.credential({
          idToken: (res.result as any).identityToken,
          rawNonce: (res.result as any).nonce,
        });

        await linkWithCredential(auth.currentUser, credential);
      } else {
        // Web: Use popup
        const provider = new OAuthProvider("apple.com");
        provider.addScope("email");
        provider.addScope("name");

        await linkWithPopup(
          auth.currentUser,
          provider,
          browserPopupRedirectResolver,
        );
      }

      // Log analytics event
      analytics.logEvent("account_linked", {
        provider: "apple",
        timestamp: new Date().toISOString(),
      });
      crashlytics.log(`User Linked Apple Account: ${auth.currentUser.uid}`);
    } catch (error: any) {
      crashlytics.logError(
        `Error linking Apple account: ${JSON.stringify(error?.message)}`,
        "AuthContext.tsx",
        error,
      );

      if (error.code === "auth/credential-already-in-use") {
        throw new Error(
          "This Apple account is already linked to another user.",
        );
      } else if (error.code === "auth/provider-already-linked") {
        throw new Error("You already have an Apple account linked.");
      } else if (error.code === "auth/popup-closed-by-user") {
        throw new Error("Link cancelled.");
      }

      throw error;
    }
  };

  const getLinkedProviders = (): UserInfo[] => {
    if (!auth.currentUser) return [];
    return auth.currentUser.providerData;
  };

  const unlinkProvider = async (providerId: string) => {
    try {
      if (!auth.currentUser) {
        throw new Error("No user is currently signed in.");
      }

      // Check if this is the only provider
      if (auth.currentUser.providerData.length <= 1) {
        throw new Error(
          "Cannot unlink your only sign-in method. Please link another account first.",
        );
      }

      await unlink(auth.currentUser, providerId);

      // Log analytics event
      analytics.logEvent("account_unlinked", {
        provider: providerId,
        timestamp: new Date().toISOString(),
      });
      crashlytics.log(
        `User Unlinked Account: ${providerId} (${auth.currentUser.uid})`,
      );
    } catch (error: any) {
      crashlytics.logError(
        `Error unlinking account: ${JSON.stringify(error?.message)}`,
        "AuthContext.tsx",
        error,
      );
      throw error;
    }
  };

  const value: AuthContextType = {
    currentUser,
    isAuthenticated: !!currentUser,
    isLoading,
    setIsLoading,
    login,
    loginWithGoogle,
    loginWithApple,
    signupWithGoogle,
    signupWithApple,
    signup,
    logout,
    deleteAccount,
    deleteAuthUser,
    authFirebaseUser,
    resendVerificationEmail,
    isEmailVerified,
    getAllUsers,
    searchUsers,
    getUserById,
    getUsersByIds,
    updateUser,
    markOnboardingComplete,
    requestFollow,
    cancelFollowRequest,
    acceptFollowRequest,
    declineFollowRequest,
    unfollowUser,
    establishMutualFollow,
    removeFollower,
    linkGoogleAccount,
    linkAppleAccount,
    getLinkedProviders,
    unlinkProvider,
    isApplePrivateRelay,
    shouldShowPrivateRelayBanner,
    dismissPrivateRelayBanner: async () => {
      if (!db || !currentUser) return;
      setShouldShowPrivateRelayBanner(false);
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.id);
        await updateDoc(userDocRef, { dismissedPrivateRelayBanner: true });
      }
    },
  };

  return (
    <>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
      <ConfirmModal
        isOpen={showSignupConfirm}
        title="Sign Up Required"
        message={signupConfirmMessage}
        onConfirm={async () => {
          setIsSigningUp(true);
          setSignupError("");
          try {
            if (signupProvider === "google") {
              await signupWithGoogle();
            } else {
              await signupWithApple();
            }
            setShowSignupConfirm(false);
          } catch (err: any) {
            setSignupError(err?.message || "Sign up failed. Please try again.");
          } finally {
            setIsSigningUp(false);
          }
        }}
        onCancel={() => setShowSignupConfirm(false)}
        confirmText="Sign Up"
        cancelText="Cancel"
        confirmDisabled={!termsChecked || isSigningUp}
      >
        <div style={{ marginTop: 16, display: "flex", alignItems: "center" }}>
          <input
            type="checkbox"
            id="terms-checkbox"
            checked={termsChecked}
            onChange={(e) => setTermsChecked(e.target.checked)}
            style={{ marginRight: 8 }}
            disabled={isSigningUp}
          />
          <label htmlFor="terms-checkbox" style={{ fontSize: 14 }}>
            I agree to the{" "}
            <a
              href="https://whoisin.sg/terms"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#007bff", textDecoration: "underline" }}
            >
              Terms and Conditions
            </a>
          </label>
        </div>
        {signupError && (
          <div style={{ color: "red", marginTop: 12, fontSize: 14 }}>
            {signupError}
          </div>
        )}
        {isSigningUp && (
          <div style={{ marginTop: 12, fontSize: 14 }}>Signing you up...</div>
        )}
      </ConfirmModal>
    </>
  );
};
